import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, TemplateRef, Injector, afterNextRender } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { async, firstValueFrom, interval, Subscription } from 'rxjs';
import { DeviceService } from 'src/app/services/device.service';
import { GroupService } from 'src/app/services/group.service';
import { SettingsService } from 'src/app/services/settings.service';
import { CommonService } from '../services/common.service';
import { TreeNodeComponent } from './tree-node.component';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { environment } from 'src/environments/environment';

interface ElementNode {
  tag: string;
  attributes: any;
  children: ElementNode[];
  bounds?: BoundsRect;
  xpath?: string;
}

interface BoundsRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

interface Locator {
  type: string;
  value: string;
}

interface RecordedElementStep {
  id: number;                      // local-only, for tracking/insertion/deletion
  step: number;                    // 1-based order, recalculated on every change
  element_name: string;
  selector_strategy: 'xpath' | 'id' | 'accessibility id' | '-android uiautomator' | 'class name' | null;
  selector_value: string | null;
  action: 'tap' | 'input' | 'long_press' | 'clear' | 'get_text' | 'is_visible' | 'keycode' | 'swipe';
  value: string;
  duration_ms: number | null;
  key_code: number | null;
  swipe_direction: string | null;
  timeout_ms: number;
  elementTag?: string | null;
  // UI-only run feedback for the single-step "run" button
  runStatus?: 'RUNNING' | 'SUCCESS' | 'FAILED' | null;
  runError?: string | null;
}

interface RecordedAction {
  index: number;
  actionType: string;
  xpath: string | null;
  elementTag: string | null;
  resourceId: string | null;
  text: string | null;
  contentDesc: string | null;
  coordinates: { x: number; y: number } | null;
  swipe: { startX: number; startY: number; endX: number; endY: number; duration: number } | null;
  timestamp: string;
}

interface ImageRegion {
  id: number;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  dataUrl: string;
  action: string;
  value: string;
  image_id: string | null;
  isUploading: boolean;
  uploadError: string | null;
  confidence: number;
  runStatus?: 'RUNNING' | 'SUCCESS' | 'FAILED' | null;
  runError?: string | null;
}

interface TestStep {
  id: number;
  action: string;
  target: string;
  inputValue: string;
  expectedResult: string;
  status: 'passed' | 'failed' | 'not-run' | 'running';
}

interface TestCase {
  id: number;
  tcNumber: string;
  name: string;
  category: string;
  status: 'passed' | 'failed' | 'not-run' | 'running';
  steps: TestStep[];
}

// Add these model interfaces if missing
export interface AutomationScript {
  script_id: string;
  script_name: string;
}

export interface ScriptExecutionStepResult {
  step_index: number;
  status: string;
  action: string;
  match_x?: number;
  match_y?: number;
  confidence?: number;
  retries?: number;
  error?: string;
  timestamp: string;
  events?: string[];
  element_name?: string;
  image_id?: string;
  script_id?: string,
  showImage?: boolean;
}


@Component({
  selector: 'app-inspector',
  templateUrl: './inspector.component.html',
  styleUrls: ['./inspector.component.scss', './icons.css'],
  standalone: true,
  imports: [TreeNodeComponent, TranslateModule, FormsModule, MatIconModule]
})
export class InspectorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sourceContainer') sourceContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('screenshotCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('overlayCanvas') overlayCanvasRef!: ElementRef<HTMLCanvasElement>;

  //alert template variables
  @ViewChild('alerttemplate') alertTemplate!: TemplateRef<any>;
  @ViewChild('alerttemplate1') alertTemplate1!: TemplateRef<any>;
  @ViewChild('alerttemplate2') alertTemplate2!: TemplateRef<any>;
  @ViewChild('alerttemplateStay') alertTemplateStay!: TemplateRef<any>;
  msg: string = "";
  modalRef!: MatDialogRef<any>;
  showCloseSessionAlert: boolean = false;

  // Element Recorder
  // ── Element Recorder — backend script binding ────────────────────────────
  recordedElementSteps: RecordedElementStep[] = [];
  private elementStepCounter = 0;

  elementScriptId: string | null = null;
  elementScriptName: string = '';
  private hasSavedElementScriptOnce: boolean = false;
  isSavingElementScript: boolean = false;
  elementScriptSaveResult: { success: boolean; message: string } | null = null;

  isPlayingAllElements: boolean = false;
  activeElementExecutionId: string | null = null;
  elementExecutionStatus: 'PENDING' | 'RUNNING' | 'PAUSED' | 'SUCCESS' | 'FAILED' | 'STOPPED' | null = null;
  elementExecutionStepResults: any[] = [];
  currentElementExecutionStep: number = 0;
  totalElementExecutionSteps: number = 0;
  private elementExecutionPollingInterval: any = null;
  private singleElementStepPollingInterval: any = null;

  // Action picker (opens when an element is selected while recording)
  showActionPicker: boolean = false;
  pendingPickerElement: ElementNode | null = null;
  pickerAction: RecordedElementStep['action'] = 'tap';
  pickerValue: string = '';
  pickerElementName: string = '';

  // System action picker (Home / Back / Recents / custom keycode)
  showSystemActionPicker: boolean = false;
  systemActionType: 'home' | 'back' | 'recents' | 'keycode' = 'home';
  systemActionKeyCode: number | null = null;
  systemActionName: string = '';

  // Insert-in-between support
  insertElementStepAtIndex: number | null = null;
  insertMenuIndex: number | null = null;

  // Image Recorder — insert-in-between support
  insertAtIndex: number | null = null;

  // Image Recorder — single-step execution polling
  private singleStepPollingInterval: any = null;

  currentUser: any;
  isRecording: boolean = false;
  recordedActions: RecordedAction[] = [];
  device: any;
  hoveredCoords: { x: number; y: number } | null = null;
  manualTapX: number | null = null;
  manualTapY: number | null = null;
  // Session & Connection
  sessionId: string = '';
  isConnected: boolean = false;
  deviceCapabilities: any = null;
  appiumServerUrl: string;
  baseUrl = environment.baseUrl;

  // Screen & Canvas
  screenshot: string = '';
  screenshotImg: HTMLImageElement | null = null;
  deviceResolution = { width: 1080, height: 1920 };
  scaleFactor = { x: 1, y: 1 };
  zoomLevel: number = 1;

  /**
   * CSS aspect-ratio value for the Mobile_wrapper.png <img>.
   * Computed directly from the canvas's own pixel dimensions
   * (not deviceResolution) so it always matches what's actually drawn.
   * Widened by 3% to leave room for the bezel either side of the screen.
   */
  canvasAspect: number | null = null;

  /**
   * Recomputes canvasAspect from the current canvas element's pixel size.
   * Call this any time canvas.width / canvas.height changes
   * (after drawScreenshot() or drawDummyScreenshot()).
   */
  private updateCanvasAspect(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !canvas.width || !canvas.height) return;

    const incWidth = canvas.width * 1.03; // widen by 3% to simulate bezel
    this.canvasAspect = incWidth / canvas.height;
  }

  // Element Tree & Selection
  pageSourceXML: Document | null = null;
  elementTree: ElementNode[] = [];
  selectedElement: ElementNode | null = null;
  hoveredElement: any = null;
  tooltipX: number = 0;
  tooltipY: number = 0;

  // Locators
  generatedLocators: Locator[] = [];

  // Actions
  textToSend: string = '';
  isPerformingAction: boolean = false;
  lastActionResult: { success: boolean; message: string } | null = null;
  expandAllFlag: boolean | null = null;

  // UI State
  isLoading: boolean = false;
  errorMessage: string = '';
  searchQuery: string = '';
  autoRefresh: boolean = false;
  private refreshSubscription?: Subscription;

  // Image Recorder
  imageSelectMode: boolean = false;
  imageRegions: ImageRegion[] = [];
  isDraggingRegion: boolean = false;
  selectionStart: { canvasX: number; canvasY: number } | null = null;
  selectionCurrent: { canvasX: number; canvasY: number } | null = null;
  imageRegionCounter: number = 0;
  isPlayingAll: boolean = false;
  playingRegionId: number | null = null;
  private shouldStopPlayback: boolean = false;

  // Image Recorder — backend script binding
  imageScriptId: string | null = null;
  imageScriptName: string = '';
  isInitializingScript: boolean = false;
  isSavingScript: boolean = false;
  scriptSaveResult: { success: boolean; message: string } | null = null;
  public activeExecutionId: string | null = null;
  public executionStatus: 'PENDING' | 'RUNNING' | 'PAUSED' | 'SUCCESS' | 'FAILED' | 'STOPPED' | null = null;
  public executionStepResults: any[] = [];
  public currentExecutionStep: number = 0;
  public totalExecutionSteps: number = 0;
  private executionPollingInterval: any = null;

  // Repository
  testCases: TestCase[] = [];
  selectedTestCase: TestCase | null = null;
  tcSearchQuery: string = '';
  public availableScripts: AutomationScript[] = [];
  public selectedScript: AutomationScript | null = null;
  public isScriptsLoading: boolean = false;
  public scriptSteps: ScriptExecutionStepResult[] = [];
  public executionLiveStatus: string | null = null;
  private liveSocket: WebSocket | null = null;
  public expandedStepIndex: number | null = null; // Tracks which step list details are expanded
  public systemLogs: string[] = [];              // Stores step-less global stream milestones
  public loadedImages: { [imageId: string]: string } = {};

  //resizer
  panelWidth = 26; // default %
  private isResizing = false;

  // Capabilities Editor
  showCapabilitiesEditor: boolean = false;
  capEditAppName: string = '';
  capEditPackageName: string = '';
  capEditRawJson: string = '';
  capJsonError: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private deviceService: DeviceService,
    private dialog: MatDialog,
    private groupService: GroupService,
    private settingsService: SettingsService,
    private commonService: CommonService,
    private injector: Injector
  ) { }

  // ngOnInit(): void {
  //   this.route.params.subscribe(async params => {
  //     let payloadAppiumSession = sessionStorage.getItem("payloadAppiumSession")
  //       ? JSON.parse(sessionStorage.getItem("payloadAppiumSession") || '{}')
  //       : null;

  //     this.device = await this.getDevice(payloadAppiumSession?.desiredCapabilities?.["appium:deviceName"]);
  //     this.appiumServerUrl = this.device?.providerUrl ?? '';
  //     this.sessionId = params['sessionId'];
  //     this.deviceCapabilities = payloadAppiumSession?.desiredCapabilities ?? null;

  //     console.log('Device:', this.device,
  //       '\nSession ID:', this.sessionId,
  //       '\nAppium URL:', this.appiumServerUrl,
  //       '\nDesired Capabilities:', this.deviceCapabilities);

  //     if (this.sessionId) {
  //       this.currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '{}');
  //       // ── Real Appium session: connect and pull live data ──────────
  //       this.initializeSession();
  //       this.fetchSavedScripts();
  //     }
  //   });
  // }

  ngOnInit(): void {
    this.route.params.subscribe(async params => {
      const serial = params['serial'];
      const sessionId = params['sessionId'];
      this.sessionId = sessionId;
      sessionStorage.setItem("appiumSessionId", sessionId);
      this.device = await this.getDevice(serial);
      if (this.device) {
        sessionStorage.setItem("providerUrl", this.device.providerUrl);
        sessionStorage.setItem("device", JSON.stringify(this.device));
      }
      this.appiumServerUrl = this.device?.providerUrl ?? '';

      if (!this.appiumServerUrl) {
        this.msg = 'Device not found or provider URL is missing. Please check the device configuration.';
        this.openAlertMod(this.alertTemplate1, this.msg);
        return;
      }

      try {
        const response = await firstValueFrom(
          this.http.get(`${this.appiumServerUrl}/session/${this.sessionId}`, {
            observe: 'response'
          })
        );

        if (response.status === 200) {
          const sessionValue = (response.body as any)?.value;

          // Build payloadAppiumSession from the live session response
          const payloadAppiumSession = {
            capabilities: { alwaysMatch: sessionValue, firstMatch: [{}] },
            desiredCapabilities: sessionValue
          };

          // Set session storage
          sessionStorage.setItem("providerUrl", this.appiumServerUrl);
          sessionStorage.setItem("payloadAppiumSession", JSON.stringify(payloadAppiumSession));
          sessionStorage.setItem("appiumSessionId", this.sessionId);

          this.sessionId = sessionStorage.getItem("appiumSessionId") ?? '';

          // Set local properties
          this.deviceCapabilities = sessionValue;

          console.log(
            'Device:', this.device,
            '\nSession ID:', this.sessionId,
            '\nAppium URL:', this.appiumServerUrl,
            '\nDesired Capabilities:', this.deviceCapabilities
          );

          this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
          this.initializeSession();
          this.fetchSavedScripts();

        }
      } catch (error: any) {
        // Handles 404, 500 and any other non-200 responses
        const errorValue = error?.error?.value;
        this.msg = errorValue?.message ?? 'An unknown error occurred. Please re-run the script.';
        this.openAlertMod(this.alertTemplate1, this.msg);
      }
    });
  }

  ngAfterViewInit(): void {
    // Drawing is triggered from loadDummyData() / initializeSession() after async init completes
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    if (this.singleStepPollingInterval) {
      clearInterval(this.singleStepPollingInterval);
      this.singleStepPollingInterval = null;
    }
    if (this.elementExecutionPollingInterval) {
      clearInterval(this.elementExecutionPollingInterval);
    }
    if (this.singleElementStepPollingInterval) {
      clearInterval(this.singleElementStepPollingInterval);
    }
    this.closeWebSocketConnection();
  }

  // resize scren-panel and right-section
  startResize(event: MouseEvent) {
    this.isResizing = true;
    document.body.classList.add('resizing');

    document.addEventListener('mousemove', this.onResize);
    document.addEventListener('mouseup', this.stopResize);
  }

  onResize = (event: MouseEvent) => {
    if (!this.isResizing) return;

    const container = document.querySelector('.screen-panel')?.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    const newWidthPx = event.clientX - containerRect.left;
    const newWidthPercent = (newWidthPx / containerRect.width) * 100;

    // ✅ Clamp between 25% and 70%
    this.panelWidth = Math.min(70, Math.max(25, newWidthPercent));
  };

  stopResize = () => {
    this.isResizing = false;
    document.body.classList.remove('resizing');

    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.stopResize);
  };

  // ── Session Initialisation (real Appium) ────────────────────────────────────

  async initializeSession(): Promise<void> {
    try {
      this.isLoading = true;
      this.errorMessage = '';

      // 1. Fetch session info → capabilities
      const sessionInfo = await this.getSessionInfo();
      // Merge with any caps already set from sessionStorage so nothing is lost
      this.deviceCapabilities = {
        ...(this.deviceCapabilities ?? {}),
        ...(sessionInfo?.capabilities ?? {}),
      };

      // 2. Get the real device resolution before taking the first screenshot
      //    so that scaleFactor is calculated correctly
      await this.getDeviceResolution();

      // 3. Pull screenshot + page source in parallel
      await this.refreshAll();

      this.isConnected = true;
    } catch (error) {
      console.error('Failed to initialize session:', error);
      this.errorMessage = 'Failed to connect to Appium session. Check the server URL and session ID.';
      this.msg = this.errorMessage;
      this.openAlertMod(this.alertTemplate, this.msg);
      this.isConnected = false;
    } finally {
      this.isLoading = false;
    }
  }

  async getSessionInfo(): Promise<any> {
    // GET /session/:sessionId  →  { value: { capabilities: { … } } }
    const url = `${this.appiumServerUrl}/session/${this.sessionId}`;
    const response = await this.http.get(url).toPromise();
    return (response as any).value;
  }

  async getDeviceResolution(): Promise<void> {
    try {
      // W3C endpoint for window rect: GET /session/:id/window/rect
      const url = `${this.appiumServerUrl}/session/${this.sessionId}/window/rect`;
      const response = await this.http.get(url).toPromise();
      const rect = (response as any).value;

      this.deviceResolution = {
        width: rect.width || 1080,
        height: rect.height || 1920,
      };
    } catch (error) {
      console.warn('Could not get device resolution via /window/rect, using defaults:', error);
      // Keep whatever defaults are set; the screenshot image size will be used
      // as a fallback when loadScreenshotImage() fires
    }
  }

  async refreshAll(): Promise<void> {
    this.isLoading = true;
    try {
      // Run both fetches in parallel for speed
      await Promise.all([
        this.refreshScreenshot(),
        this.refreshPageSource(),
      ]);
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // ── Screenshot ──────────────────────────────────────────────────────────────

  async refreshScreenshot(retries = 3): Promise<void> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        // GET /session/:id/screenshot  →  { value: "<base64 PNG>" }
        const url = `${this.appiumServerUrl}/session/${this.sessionId}/screenshot`;
        const response = await this.http.get(url).toPromise();
        const base64Screenshot = (response as any).value;

        this.screenshot = base64Screenshot;
        await this.loadScreenshotImage(base64Screenshot);

        if (this.selectedElement) {
          this.drawHighlight(this.selectedElement);
        }
        return; // success

      } catch (error) {
        console.warn(`Screenshot attempt ${attempt} failed:`, error);
        if (attempt === retries) {
          this.errorMessage = 'Failed to load screenshot';
        }
      }
    }
  }

  // ── Page Source ─────────────────────────────────────────────────────────────

  async refreshPageSource(): Promise<void> {
    try {
      // GET /session/:id/source  →  { value: "<XML string>" }
      const url = `${this.appiumServerUrl}/session/${this.sessionId}/source`;
      const response = await this.http.get(url).toPromise();
      const xmlString = (response as any).value;

      const parser = new DOMParser();
      this.pageSourceXML = parser.parseFromString(xmlString, 'text/xml');

      const root = this.pageSourceXML.documentElement;

      // AppiumAUT is the iOS wrapper root — check root OR its first child
      const tagToCheck = root.tagName === 'AppiumAUT'
        ? (root.children[0]?.tagName || '')
        : root.tagName;

      const isIOSSource = tagToCheck.includes('XCUIElement');

      console.log('Root tag:', root.tagName,
        'First child tag:', root.children[0]?.tagName,
        '→ isIOS:', isIOSSource);

      this.elementTree = this.buildElementTree(root, '', isIOSSource);

      console.log('Tree built, sample node:',
        this.elementTree[0]?.tag,
        'bounds:', this.elementTree[0]?.bounds);

    } catch (error) {
      console.error('Failed to get page source:', error);
    }
  }

  // ── Navigation helpers ──────────────────────────────────────────────────────

  async navigateBack(): Promise<void> {
    try {
      if (this.isIOS()) {
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/execute`, {
          script: 'mobile: swipe', args: [{ direction: 'right' }]
        }));
      } else {
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/appium/device/press_keycode`,
          { keycode: 4 }));
      }
      setTimeout(() => this.refreshAll(), 500);
    } catch (error) { this.showActionResult(false, `Back failed: ${error}`); }
  }

  async navigateHome(): Promise<void> {
    try {
      if (this.isIOS()) {
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/execute`, {
          script: 'mobile: pressButton', args: [{ name: 'home' }]
        }));
      } else {
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/appium/device/press_keycode`,
          { keycode: 3 }));
      }
      setTimeout(() => this.refreshAll(), 500);
    } catch (error) { this.showActionResult(false, `Home failed: ${error}`); }
  }

  async navigateRecents(): Promise<void> {
    try {
      if (this.isIOS()) {
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/execute`, {
          script: 'mobile: pressButton', args: [{ name: 'home' }]
        }));
        await new Promise(r => setTimeout(r, 300));
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/execute`, {
          script: 'mobile: pressButton', args: [{ name: 'home' }]
        }));
      } else {
        await firstValueFrom(this.http.post(`${this.appiumServerUrl}/session/${this.sessionId}/appium/device/press_keycode`,
          { keycode: 187 }));
      }
      setTimeout(() => this.refreshAll(), 500);
    } catch (error) { this.showActionResult(false, `Recents failed: ${error}`); }
  }

  private generateLocators(element: ElementNode): void {
    this.generatedLocators = [];
    if (element.attributes['resource-id']) {
      this.generatedLocators.push({ type: 'ID', value: element.attributes['resource-id'] });
    }
    if (element.xpath) {
      this.generatedLocators.push({ type: 'XPath', value: element.xpath });
    }
    if (element.attributes['content-desc']) {
      this.generatedLocators.push({ type: 'Accessibility ID', value: element.attributes['content-desc'] });
    }
    if (element.attributes.class) {
      this.generatedLocators.push({ type: 'Class Name', value: element.attributes.class });
    }
    if (element.attributes.text) {
      this.generatedLocators.push({ type: 'Text', value: element.attributes.text });
    }
  }

  private drawDummyScreenshot(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    const W = 540, H = 1170;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rr = (x: number, y: number, w: number, h: number, r: number) => this._roundRect(ctx, x, y, w, h, r);

    // ── Background ──────────────────────────────────────────────────
    ctx.fillStyle = '#F2F2F7';
    ctx.fillRect(0, 0, W, H);

    // ── Status bar ──────────────────────────────────────────────────
    ctx.fillStyle = '#141d48';
    ctx.fillRect(0, 0, W, 32);

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 12px Arial';
    ctx.fillText('12:20', 14, 22);

    // Signal bars
    ctx.fillStyle = '#FFFFFF';
    [4, 7, 10, 13].forEach((bh, i) => {
      ctx.fillRect(W - 70 + i * 7, 22 - bh, 5, bh);
    });

    // Wifi arcs
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1.5;
    [5, 9, 13].forEach(radius => {
      ctx.beginPath();
      ctx.arc(W - 90, 24, radius, Math.PI * 1.25, Math.PI * 1.75);
      ctx.stroke();
    });
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath(); ctx.arc(W - 90, 24, 2, 0, Math.PI * 2); ctx.fill();

    // Battery
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(W - 38, 10, 20, 11);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(W - 18, 13, 3, 5);
    ctx.fillRect(W - 37, 11, 16, 9);

    // ── Toolbar ─────────────────────────────────────────────────────
    ctx.fillStyle = '#141d48';
    ctx.fillRect(0, 32, W, 70);

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px Arial';
    ctx.fillText('Settings', 18, 82);

    // Avatar
    ctx.fillStyle = '#4A90D9';
    ctx.beginPath(); ctx.arc(W - 32, 66, 20, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('S', W - 38, 72);

    // ── Search Bar ──────────────────────────────────────────────────
    ctx.fillStyle = '#FFFFFF';
    rr(14, 112, W - 28, 44, 22); ctx.fill();

    // Magnifying glass (drawn, no emoji)
    ctx.strokeStyle = '#AEAEB2';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(40, 134, 9, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(47, 141); ctx.lineTo(55, 149); ctx.stroke();

    ctx.fillStyle = '#AEAEB2';
    ctx.font = '13px Arial';
    ctx.fillText('Search settings', 62, 139);

    // ── Settings rows ────────────────────────────────────────────────
    const rows: { color: string; title: string; sub: string; y: number }[] = [
      { color: '#167FFC', title: 'Network & internet', sub: 'Mobile, Wi-Fi, hotspot', y: 168 },
      { color: '#30B0C7', title: 'Connected devices', sub: 'Bluetooth, NFC', y: 252 },
      { color: '#34C759', title: 'Apps', sub: 'Assistant, recent apps', y: 336 },
      { color: '#FF9500', title: 'Notifications', sub: 'Notification history, DND', y: 420 },
      { color: '#30D158', title: 'Battery', sub: '78% — About 14h 30m left', y: 504 },
      { color: '#5856D6', title: 'Storage', sub: '32 GB used of 256 GB', y: 588 },
      { color: '#FF3B30', title: 'Sound & vibration', sub: 'Volume, ringtone', y: 672 },
      { color: '#5AC8FA', title: 'Display', sub: 'Brightness, dark mode', y: 756 },
    ];

    rows.forEach((row, i) => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, row.y, W, 82);

      if (i < rows.length - 1) {
        ctx.fillStyle = '#E5E5EA';
        ctx.fillRect(72, row.y + 81, W - 72, 1);
      }

      // Colored icon square
      ctx.fillStyle = row.color;
      rr(14, row.y + 17, 42, 42, 10); ctx.fill();

      // White icon shape
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      this._drawSettingsIcon(ctx, i, 35, row.y + 38);

      // Title
      ctx.fillStyle = '#1C1C1E';
      ctx.font = 'bold 14px Arial';
      ctx.fillText(row.title, 70, row.y + 36);

      // Subtitle
      ctx.fillStyle = '#8E8E93';
      ctx.font = '12px Arial';
      ctx.fillText(row.sub, 70, row.y + 55);

      // Chevron
      ctx.strokeStyle = '#C7C7CC';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W - 28, row.y + 33);
      ctx.lineTo(W - 20, row.y + 41);
      ctx.lineTo(W - 28, row.y + 49);
      ctx.stroke();
    });

    // ── Sign In button (selected / highlighted) ──────────────────────
    const btnY = 862;

    // Blue selection glow border
    ctx.strokeStyle = 'rgba(22, 127, 252, 0.45)';
    ctx.lineWidth = 4;
    rr(52, btnY - 4, W - 104, 64, 14); ctx.stroke();

    // Button fill
    ctx.fillStyle = '#167FFC';
    rr(56, btnY, W - 112, 56, 11); ctx.fill();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 16px Arial';
    const btnText = 'Sign In';
    const tw = ctx.measureText(btnText).width;
    ctx.fillText(btnText, (W - tw) / 2, btnY + 34);

    // ── Google account section below button ──────────────────────────
    ctx.fillStyle = '#8E8E93';
    ctx.font = '11px Arial';
    const hint = 'Sign in to sync your preferences across devices';
    const hw = ctx.measureText(hint).width;
    ctx.fillText(hint, (W - hw) / 2, btnY + 76);

    // Store result
    const dataUrl = canvas.toDataURL('image/png');
    const img = new Image();
    img.onload = () => { this.screenshotImg = img; };
    img.src = dataUrl;

    this.scaleFactor = { x: 0.5, y: 0.5 };
    this.updateCanvasAspect();
  }

  private _drawSettingsIcon(ctx: CanvasRenderingContext2D, index: number, cx: number, cy: number): void {
    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;

    switch (index) {
      case 0: // Network — wifi arcs
        [5, 10, 15].forEach(r => {
          ctx.beginPath();
          ctx.arc(cx, cy + 6, r, Math.PI * 1.25, Math.PI * 1.75);
          ctx.stroke();
        });
        ctx.beginPath(); ctx.arc(cx, cy + 7, 2.5, 0, Math.PI * 2); ctx.fill();
        break;

      case 1: // Bluetooth zigzag
        ctx.beginPath();
        ctx.moveTo(cx - 6, cy - 8); ctx.lineTo(cx + 6, cy + 5);
        ctx.lineTo(cx - 6, cy + 12); ctx.lineTo(cx + 6, cy - 1);
        ctx.lineTo(cx - 6, cy - 8);
        ctx.stroke();
        break;

      case 2: // Apps — 3×3 dot grid
        for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) {
          ctx.beginPath();
          ctx.arc(cx - 8 + c * 8, cy - 7 + r * 8, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
        break;

      case 3: // Notifications — bell
        ctx.beginPath();
        ctx.arc(cx, cy - 5, 9, Math.PI * 1.1, Math.PI * 1.9);
        ctx.lineTo(cx + 10, cy + 8);
        ctx.lineTo(cx - 10, cy + 8);
        ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.arc(cx, cy + 11, 3, 0, Math.PI * 2); ctx.fill();
        break;

      case 4: // Battery
        ctx.strokeRect(cx - 9, cy - 7, 16, 12);
        ctx.fillRect(cx + 7, cy - 4, 3, 6);   // tip
        ctx.fillRect(cx - 8, cy - 6, 12, 10); // charge fill
        break;

      case 5: // Storage — stacked bars
        ctx.fillRect(cx - 11, cy - 8, 22, 5);
        ctx.fillRect(cx - 11, cy, 22, 5);
        ctx.fillRect(cx - 11, cy + 8, 22, 5);
        break;

      case 6: // Sound — speaker + wave
        ctx.fillRect(cx - 9, cy - 4, 5, 9);
        ctx.beginPath();
        ctx.moveTo(cx - 4, cy - 4); ctx.lineTo(cx + 4, cy - 10);
        ctx.lineTo(cx + 4, cy + 10); ctx.lineTo(cx - 4, cy + 4);
        ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.arc(cx + 4, cy, 8, -0.55, 0.55); ctx.stroke();
        break;

      case 7: // Display — monitor outline
        ctx.strokeRect(cx - 11, cy - 8, 22, 15);
        ctx.fillRect(cx - 5, cy + 7, 10, 4);
        ctx.fillRect(cx - 8, cy + 10, 16, 2);
        break;
    }
  }

  private _roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  async getDevice(serial: string): Promise<any> {
    try {
      const device: any = await firstValueFrom(this.deviceService.get(serial));

      const invitedDevice = this.groupService.invite(device);

      this.settingsService.set('lastUsedDevice', serial);

      return device;
    } catch (error) {
      console.error('Error getting device:', error);
      return null;
    }
  }

  private loadScreenshotImage(base64: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = `data:image/png;base64,${base64}`;
      img.onload = () => {
        this.screenshotImg = img;

        // If /window/rect failed and we still have defaults, use the image's
        // own pixel dimensions as the authoritative device resolution so
        // scaleFactor stays at exactly 1:1 and element bounds line up correctly.
        if (
          this.deviceResolution.width === 1080 &&
          this.deviceResolution.height === 1920 &&
          (img.width !== 1080 || img.height !== 1920)
        ) {
          this.deviceResolution = { width: img.width, height: img.height };
        }

        this.scaleFactor = {
          x: img.width / this.deviceResolution.width,
          y: img.height / this.deviceResolution.height,
        };

        queueMicrotask(() => this.drawScreenshot());
        resolve();
      };
      img.onerror = () => reject(new Error('Failed to load screenshot image'));
    });
  }

  private drawScreenshot(): void {
    if (!this.screenshotImg || !this.canvasRef?.nativeElement) {
      requestAnimationFrame(() => this.drawScreenshot());
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = this.screenshotImg.width;
    canvas.height = this.screenshotImg.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.screenshotImg, 0, 0);

    this.updateCanvasAspect();
  }

  private isIOS(): boolean {
    if (this.pageSourceXML) {
      const xml = this.pageSourceXML;
      const root = xml.documentElement;

      // Root is AppiumAUT — check its first child instead
      const firstChild = root.tagName === 'AppiumAUT'
        ? root.children[0]
        : root;

      if (firstChild?.tagName?.includes('XCUIElement') ||
        firstChild?.tagName?.includes('XCUIElementType')) {
        return true;
      }

      // Also check root itself
      if (root.tagName.includes('XCUIElement')) return true;
    }

    // Fallback: check elementTree tags
    if (this.elementTree?.length > 0) {
      const firstTag = this.elementTree[0]?.tag || '';
      if (firstTag.includes('XCUIElement')) return true;
    }

    // Fallback: capabilities
    const caps = this.deviceCapabilities;
    const platform = (
      caps?.platformName ||
      caps?.['appium:platformName'] ||
      caps?.desired?.platformName ||
      caps?.capabilities?.platformName || ''
    ).toLowerCase();

    return platform === 'ios';
  }

  private buildElementTree(
    xmlNode: Element,
    path: string = '',
    isIOS: boolean = false
  ): ElementNode[] {
    const nodes: ElementNode[] = [];

    for (let i = 0; i < xmlNode.children.length; i++) {
      const child = xmlNode.children[i];
      const currentPath = path + '/' + child.tagName + '[' + (i + 1) + ']';

      const node: ElementNode = {
        tag: child.tagName,
        attributes: this.extractAttributes(child),
        children: this.buildElementTree(child, currentPath, isIOS),
        xpath: currentPath
      };

      // Android: bounds as "[x1,y1][x2,y2]" string
      if (node.attributes.bounds) {
        node.bounds = this.parseBounds(node.attributes.bounds);
      }
      // iOS: bounds as separate x, y, width, height attributes
      else if (isIOS) {
        node.bounds = this.parseIOSBounds(node.attributes);
      }

      nodes.push(node);
    }

    return nodes;
  }

  private extractAttributes(element: Element): any {
    const attrs: any = {};
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      attrs[attr.name] = attr.value;
    }
    return attrs;
  }

  private parseBounds(boundsString: string): BoundsRect | undefined {
    const androidRegex = /\[(\d+),(\d+)\]\[(\d+),(\d+)\]/;
    const match = boundsString.match(androidRegex);

    if (!match) return undefined;

    const left = parseInt(match[1]);
    const top = parseInt(match[2]);
    const right = parseInt(match[3]);
    const bottom = parseInt(match[4]);

    return {
      left, top, right, bottom,
      width: right - left,
      height: bottom - top,
      centerX: (left + right) / 2,
      centerY: (top + bottom) / 2
    };
  }

  private parseIOSBounds(attrs: any): BoundsRect | undefined {
    const x = parseFloat(attrs['x']);
    const y = parseFloat(attrs['y']);
    const width = parseFloat(attrs['width']);
    const height = parseFloat(attrs['height']);

    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
      return undefined;
    }

    if (width === 0 && height === 0) {
      return undefined;
    }

    return {
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
      width,
      height,
      centerX: x + width / 2,
      centerY: y + height / 2
    };
  }

  onElementSelect(element: ElementNode): void {
    this.selectedElement = element;
    this.generateLocators(element);
    this.drawHighlight(element);
    const needsExpand = !this.expandAllFlag;
    if (needsExpand) {
      this.expandAllFlag = true;
    }
    afterNextRender(() => this.scrollToSelectedNode(element), { injector: this.injector });

    if (this.isRecording && this.activeTab === 'recorder') {
      this.openActionPicker(element);
    }
  }

  private scrollToSelectedNode(node: ElementNode) {
    const container = this.sourceContainer?.nativeElement;
    if (!container) return;

    // Match by exact attribute value rather than building a CSS selector
    // from the xpath string — avoids escaping headaches with [, ], @, ', " etc.
    const target = Array.from(
      container.querySelectorAll<HTMLElement>('[data-xpath]')
    ).find(el => el.getAttribute('data-xpath') === node.xpath);

    if (!target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const targetOffsetWithinContainer =
      (targetRect.top - containerRect.top) + container.scrollTop;

    const centeredTop =
      targetOffsetWithinContainer - (container.clientHeight / 2) + (targetRect.height / 2);

    container.scrollTo({
      top: Math.max(0, centeredTop),
      behavior: 'smooth'
    });
  }

  onElementHover(element: ElementNode): void {
    this.hoveredElement = element;
  }

  private drawHighlight(element: ElementNode): void {
    if (!element.bounds || !this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx || !this.screenshotImg) return;

    // Force frame boundaries reset to clean native dimensions 
    canvas.width = this.screenshotImg.width;
    canvas.height = this.screenshotImg.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.screenshotImg, 0, 0);
    this.updateCanvasAspect();

    const bounds = {
      left: element.bounds.left * this.scaleFactor.x,
      top: element.bounds.top * this.scaleFactor.y,
      width: element.bounds.width * this.scaleFactor.x,
      height: element.bounds.height * this.scaleFactor.y
    };

    ctx.strokeStyle = '#FF4444';
    ctx.lineWidth = 3;
    ctx.strokeRect(bounds.left, bounds.top, bounds.width, bounds.height);

    ctx.fillStyle = 'rgba(255, 68, 68, 0.2)';
    ctx.fillRect(bounds.left, bounds.top, bounds.width, bounds.height);
  }

  onScreenClick(event: MouseEvent): void {
    if (!this.canvasRef) return;

    // Optional conditional check for background data update refreshing
    if (this.autoRefresh) {
      this.refreshAll();
    }

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const displayScale = {
      x: canvas.width / rect.width,
      y: canvas.height / rect.height
    };

    const canvasX = clickX * displayScale.x;
    const canvasY = clickY * displayScale.y;

    const deviceX = canvasX / this.scaleFactor.x;
    const deviceY = canvasY / this.scaleFactor.y;

    console.log('Click Context Coordinate Maps:', { clickX, clickY, canvasX, canvasY, deviceX, deviceY });

    const element = this.findElementAtPoint(deviceX, deviceY);
    if (element) {
      this.onElementSelect(element);
    }
  }

  onScreenHover(event: MouseEvent): void {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    this.tooltipX = event.clientX - rect.left + 10;
    this.tooltipY = event.clientY - rect.top + 10;

    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const displayScale = {
      x: canvas.width / rect.width,
      y: canvas.height / rect.height
    };

    const canvasX = clickX * displayScale.x;
    const canvasY = clickY * displayScale.y;

    const deviceX = canvasX / this.scaleFactor.x;
    const deviceY = canvasY / this.scaleFactor.y;

    const element = this.findElementAtPoint(deviceX, deviceY);
    this.hoveredElement = element;

    this.hoveredCoords = { x: Math.round(deviceX), y: Math.round(deviceY) };
  }

  clearHoverHighlight(): void {
    this.hoveredElement = null;
    this.hoveredCoords = null;
  }

  private findElementAtPoint(x: number, y: number): ElementNode | null {
    const allElements = this.flattenTree(this.elementTree);

    let bestMatch: ElementNode | null = null;
    let smallestArea = Infinity;

    for (const element of allElements) {
      if (!element.bounds) continue;

      const bounds = element.bounds;

      if (x >= bounds.left && x <= bounds.right &&
        y >= bounds.top && y <= bounds.bottom) {

        const area = bounds.width * bounds.height;

        if (area < smallestArea) {
          smallestArea = area;
          bestMatch = element;
        }
      }
    }

    return bestMatch;
  }

  private flattenTree(nodes: ElementNode[]): ElementNode[] {
    let result: ElementNode[] = [];

    for (const node of nodes) {
      result.push(node);
      if (node.children && node.children.length > 0) {
        result = result.concat(this.flattenTree(node.children));
      }
    }

    return result;
  }

  async tapByCoordinates(x: number, y: number): Promise<void> {
    this.isPerformingAction = true;
    this.lastActionResult = null;

    try {
      const url = `${this.appiumServerUrl}/session/${this.sessionId}/actions`;
      const payload = {
        actions: [{
          type: 'pointer',
          id: 'finger1',
          parameters: { pointerType: 'touch' },
          actions: [
            { type: 'pointerMove', duration: 0, x: Math.round(x), y: Math.round(y) },
            { type: 'pointerDown', button: 0 },
            { type: 'pause', duration: 100 },
            { type: 'pointerUp', button: 0 }
          ]
        }]
      };
      this.recordAction('tapByCoordinates', this.selectedElement, { x: Math.round(x), y: Math.round(y) });
      await this.http.post(url, payload).toPromise();
      this.showActionResult(true, `Tapped at (${Math.round(x)}, ${Math.round(y)})`);
      setTimeout(() => this.refreshAll(), 500);
    } catch (error) {
      this.showActionResult(false, `Tap failed: ${error}`);
    } finally {
      this.isPerformingAction = false;
    }
  }

  async swipeByCoordinates(
    startX: number, startY: number,
    endX: number, endY: number,
    duration: number = 500
  ): Promise<void> {
    this.isPerformingAction = true;
    this.lastActionResult = null;

    try {
      const url = `${this.appiumServerUrl}/session/${this.sessionId}/actions`;
      const payload = {
        actions: [{
          type: 'pointer',
          id: 'finger1',
          parameters: { pointerType: 'touch' },
          actions: [
            { type: 'pointerMove', duration: 0, x: Math.round(startX), y: Math.round(startY) },
            { type: 'pointerDown', button: 0 },
            { type: 'pointerMove', duration, x: Math.round(endX), y: Math.round(endY) },
            { type: 'pointerUp', button: 0 }
          ]
        }]
      };
      this.recordAction('swipeByCoordinates', null, null, {
        startX: Math.round(startX), startY: Math.round(startY),
        endX: Math.round(endX), endY: Math.round(endY),
        duration
      });
      await this.http.post(url, payload).toPromise();
      this.showActionResult(true, `Swiped (${Math.round(startX)},${Math.round(startY)}) → (${Math.round(endX)},${Math.round(endY)})`);
      setTimeout(() => this.refreshAll(), 500);
    } catch (error) {
      this.showActionResult(false, `Swipe failed: ${error}`);
    } finally {
      this.isPerformingAction = false;
    }
  }

  tapAtHoveredCoords(): void {
    if (this.hoveredCoords) {
      this.tapByCoordinates(this.hoveredCoords.x, this.hoveredCoords.y);
    }
  }

  onManualCoordTap(): void {
    if (this.manualTapX !== null && this.manualTapY !== null) {
      this.tapByCoordinates(this.manualTapX, this.manualTapY);
    }
  }

  // Swipe form state
  swipeStartX: number = 0;
  swipeStartY: number = 0;
  swipeEndX: number = 0;
  swipeEndY: number = 0;
  swipeDuration: number = 500;

  onManualCoordSwipe(): void {
    this.swipeByCoordinates(
      this.swipeStartX, this.swipeStartY,
      this.swipeEndX, this.swipeEndY,
      this.swipeDuration
    );
  }

  async performAction(action: string): Promise<void> {
    if (!this.selectedElement || !this.selectedElement.bounds) return;

    this.isPerformingAction = true;
    this.lastActionResult = null;

    try {
      switch (action) {
        case 'tap':
          await this.tapElement();
          this.showActionResult(true, 'Tap executed successfully');
          break;
        case 'longPress':
          await this.longPressElement();
          this.showActionResult(true, 'Long press executed successfully');
          break;
        case 'sendKeys':
          await this.sendKeysToElement();
          this.showActionResult(true, 'Text sent successfully');
          break;
        case 'clear':
          await this.clearElement();
          this.showActionResult(true, 'Element cleared successfully');
          break;
        default:
          break;
      }

      setTimeout(() => this.refreshAll(), 500);
    } catch (error) {
      console.error('Action failed:', error);
      this.showActionResult(false, `Action failed: ${error}`);
    } finally {
      this.isPerformingAction = false;
    }
  }

  private async tapElement(): Promise<void> {
    const bounds = this.selectedElement!.bounds!;
    const url = `${this.appiumServerUrl}/session/${this.sessionId}/actions`;

    const payload = {
      actions: [{
        type: 'pointer',
        id: 'finger1',
        parameters: { pointerType: 'touch' },
        actions: [
          { type: 'pointerMove', duration: 0, x: Math.round(bounds.centerX), y: Math.round(bounds.centerY) },
          { type: 'pointerDown', button: 0 },
          { type: 'pause', duration: 100 },
          { type: 'pointerUp', button: 0 }
        ]
      }]
    };
    this.recordAction('tap', this.selectedElement, {
      x: Math.round(bounds.centerX),
      y: Math.round(bounds.centerY)
    });
    await this.http.post(url, payload).toPromise();
  }

  private async longPressElement(): Promise<void> {
    const bounds = this.selectedElement!.bounds!;
    const url = `${this.appiumServerUrl}/session/${this.sessionId}/actions`;

    const payload = {
      actions: [{
        type: 'pointer',
        id: 'finger1',
        parameters: { pointerType: 'touch' },
        actions: [
          { type: 'pointerMove', duration: 0, x: Math.round(bounds.centerX), y: Math.round(bounds.centerY) },
          { type: 'pointerDown', button: 0 },
          { type: 'pause', duration: 1000 },
          { type: 'pointerUp', button: 0 }
        ]
      }]
    };
    this.recordAction('longPress', this.selectedElement, {
      x: Math.round(bounds.centerX),
      y: Math.round(bounds.centerY)
    });
    await this.http.post(url, payload).toPromise();
  }

  private async sendKeysToElement(): Promise<void> {
    if (!this.textToSend) return;
    this.recordAction('sendKeys', this.selectedElement, null);
    if (this.recordedActions.length > 0) {
      this.recordedActions[this.recordedActions.length - 1] = {
        ...this.recordedActions[this.recordedActions.length - 1],
        text: this.textToSend
      };
    }

    const elementId = await this.findElement();
    if (!elementId) {
      throw new Error('Element not found');
    }

    const url = `${this.appiumServerUrl}/session/${this.sessionId}/element/${elementId}/value`;
    await this.http.post(url, { text: this.textToSend }).toPromise();

    this.textToSend = '';
  }

  private async clearElement(): Promise<void> {
    const elementId = await this.findElement();
    if (!elementId) {
      throw new Error('Element not found');
    }

    const url = `${this.appiumServerUrl}/session/${this.sessionId}/element/${elementId}/clear`;
    this.recordAction('clear', this.selectedElement);
    await this.http.post(url, {}).toPromise();
  }

  private async findElement(): Promise<string | null> {
    try {
      const url = `${this.appiumServerUrl}/session/${this.sessionId}/element`;
      let using: string;
      let value: string | undefined;

      if (this.isIOS()) {
        value = this.selectedElement?.attributes['name']
          || this.selectedElement?.attributes['label']
          || this.selectedElement?.attributes['value'];
        using = 'accessibility id';
      } else {
        if (this.selectedElement?.attributes['resource-id']) {
          value = this.selectedElement.attributes['resource-id'];
          using = 'id';
        } else if (this.selectedElement?.attributes['content-desc']) {
          value = this.selectedElement.attributes['content-desc'];
          using = 'accessibility id';
        } else if (this.selectedElement?.attributes['text']) {
          value = this.selectedElement.attributes['text'];
          using = '-android uiautomator';
          value = `new UiSelector().text("${value}")`;
        } else if (this.selectedElement?.xpath) {
          value = this.selectedElement.xpath;
          using = 'xpath';
        }
      }

      if (!value) return null;

      const response = await this.http.post(url, { using, value }).toPromise();
      const result = response as any;
      return result.value.ELEMENT
        || result.value['element-6066-11e4-a52e-4f735466cecf'];

    } catch (error) {
      console.error('Element not found:', error);
      return null;
    }
  }

  async performSwipe(direction: string): Promise<void> {
    if (!this.selectedElement || !this.selectedElement.bounds) return;

    this.isPerformingAction = true;

    try {
      const bounds = this.selectedElement.bounds;
      let startX = bounds.centerX;
      let startY = bounds.centerY;
      let endX = startX;
      let endY = startY;

      const swipeDistance = 200;

      switch (direction) {
        case 'up': endY -= swipeDistance; break;
        case 'down': endY += swipeDistance; break;
        case 'left': endX -= swipeDistance; break;
        case 'right': endX += swipeDistance; break;
      }

      const url = `${this.appiumServerUrl}/session/${this.sessionId}/actions`;
      const payload = {
        actions: [{
          type: 'pointer',
          id: 'finger1',
          parameters: { pointerType: 'touch' },
          actions: [
            { type: 'pointerMove', duration: 0, x: Math.round(startX), y: Math.round(startY) },
            { type: 'pointerDown', button: 0 },
            { type: 'pointerMove', duration: 500, x: Math.round(endX), y: Math.round(endY) },
            { type: 'pointerUp', button: 0 }
          ]
        }]
      };
      this.recordAction('swipe', this.selectedElement, null, {
        startX: Math.round(startX), startY: Math.round(startY),
        endX: Math.round(endX), endY: Math.round(endY),
        duration: 500
      });
      await this.http.post(url, payload).toPromise();
      this.showActionResult(true, `Swipe ${direction} executed successfully`);

      setTimeout(() => this.refreshAll(), 500);
    } catch (error) {
      console.error('Swipe failed:', error);
      this.showActionResult(false, `Swipe failed: ${error}`);
    } finally {
      this.isPerformingAction = false;
    }
  }

  onEnterSendKeys(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.performAction('sendKeys');
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    });
  }

  private showActionResult(success: boolean, message: string): void {
    this.lastActionResult = { success, message };

    setTimeout(() => {
      this.lastActionResult = null;
    }, 3000);
  }

  activeTab: string = 'source';

  setTab(tab: string): void {
    this.activeTab = tab;
  }

  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  expandAll(): void {
    this.expandCollapseAll(this.elementTree, true);
  }

  collapseAll(): void {
    this.expandCollapseAll(this.elementTree, false);
  }

  private expandCollapseAll(nodes: ElementNode[], expand: boolean): void {
    this.expandAllFlag = expand;
    setTimeout(() => {
      this.expandAllFlag = null;
    }, 100);
  }

  filterTree(): void {
    if (!this.pageSourceXML) return;

    const root = this.pageSourceXML.documentElement;
    const tagToCheck = root.tagName === 'AppiumAUT'
      ? (root.children[0]?.tagName || '')
      : root.tagName;
    const isIOSSource = tagToCheck.includes('XCUIElement');

    const fullTree = this.buildElementTree(root, '', isIOSSource);

    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.elementTree = fullTree;
      return;
    }

    this.elementTree = this.filterNodes(fullTree,
      this.searchQuery.toLowerCase().trim()
    );
  }

  private filterNodes(nodes: ElementNode[], query: string): ElementNode[] {
    const filtered: ElementNode[] = [];

    for (const node of nodes) {
      const matchesSearch = this.nodeMatchesQuery(node, query);
      const filteredChildren = this.filterNodes(node.children, query);

      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren
        });
      }
    }

    return filtered;
  }

  private nodeMatchesQuery(node: ElementNode, query: string): boolean {
    const searchableText = [
      node.tag,
      node.attributes['resource-id'],
      node.attributes.text,
      node.attributes['content-desc'],
      node.attributes.class
    ].filter(Boolean).join(' ').toLowerCase();

    return searchableText.includes(query);
  }

  toggleRecording(): void {
    this.isRecording = !this.isRecording;
  }

  clearRecording(): void {
    this.recordedActions = [];
    this.recordedElementSteps = [];

    if (this.hasSavedElementScriptOnce && this.elementScriptId) {
      const url = `${this.baseUrl}/imageRecord/v1/element/record/script/${this.elementScriptId}`;
      this.http.delete(url).subscribe({
        next: () => {
          this.hasSavedElementScriptOnce = false;
          this.elementScriptId = null;
        },
        error: (err) => {
          this.msg = err;
          this.openAlertMod(this.alertTemplateStay, this.msg);
          console.error('Failed to delete element script:', err)
        }
      });
    }
  }

  exportRecording(): void {
    let payloadAppiumSession = sessionStorage.getItem("payloadAppiumSession")
      ? JSON.parse(sessionStorage.getItem("payloadAppiumSession") || '{}')
      : null;
    let deviceCapabilities = payloadAppiumSession?.desiredCapabilities ?? null;

    const exportData = {
      deviceCaps: deviceCapabilities,
      script_id: this.elementScriptId,
      script_name: this.elementScriptName,
      steps: this.recordedElementSteps.map(s => ({
        step: s.step,
        element_name: s.element_name,
        selector_strategy: s.selector_strategy,
        selector_value: s.selector_value,
        action: s.action,
        value: s.value || '',
        duration_ms: s.duration_ms,
        key_code: s.key_code,
        swipe_direction: s.swipe_direction,
        timeout_ms: s.timeout_ms,
      })),
    };

    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recording_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private recordAction(
    actionType: string,
    element: ElementNode | null = null,
    coords: { x: number; y: number } | null = null,
    swipe: { startX: number; startY: number; endX: number; endY: number; duration: number } | null = null
  ): void {
    if (!this.isRecording) return;

    this.recordedActions.push({
      index: this.recordedActions.length + 1,
      actionType,
      xpath: element?.xpath ?? null,
      elementTag: element?.tag ?? null,
      resourceId: element?.attributes?.['resource-id'] ?? null,
      text: element?.attributes?.text ?? null,
      contentDesc: element?.attributes?.['content-desc'] ?? null,
      coordinates: coords,
      swipe,
      timestamp: new Date().toISOString()
    });
  }

  // ── Element Recorder — step construction ──────────────────────────────────

  trackByStepId(index: number, step: RecordedElementStep): number {
    return step.id;
  }

  getStepActionIcon(action: string): string {
    switch (action) {
      case 'tap': return 'fa-hand-pointer';
      case 'input': return 'fa-keyboard';
      case 'long_press': return 'fa-hand';
      case 'clear': return 'fa-eraser';
      case 'get_text': return 'fa-font';
      case 'is_visible': return 'fa-eye';
      case 'keycode': return 'fa-microchip';
      case 'swipe': return 'fa-arrows-alt';
      default: return 'fa-bolt';
    }
  }

  private deriveSelector(element: ElementNode): { strategy: RecordedElementStep['selector_strategy']; value: string } {
    if (this.isIOS()) {
      const value = element.attributes?.['name'] || element.attributes?.['label'] || element.attributes?.['value'];
      if (value) return { strategy: 'accessibility id', value };
    } else {
      if (element.attributes?.['resource-id']) {
        return { strategy: 'id', value: element.attributes['resource-id'] };
      }
      if (element.attributes?.['content-desc']) {
        return { strategy: 'accessibility id', value: element.attributes['content-desc'] };
      }
      if (element.attributes?.text) {
        return { strategy: '-android uiautomator', value: `new UiSelector().text("${element.attributes.text}")` };
      }
    }
    return { strategy: 'xpath', value: element.xpath || '' };
  }

  private renumberElementSteps(): void {
    this.recordedElementSteps = this.recordedElementSteps.map((s, i) => ({ ...s, step: i + 1 }));
  }

  private insertStepIntoList(step: RecordedElementStep, atIndex: number | null): void {
    const updated = [...this.recordedElementSteps];
    if (atIndex !== null && atIndex >= 0 && atIndex <= updated.length) {
      updated.splice(atIndex, 0, step);
    } else {
      updated.push(step);
    }
    this.recordedElementSteps = updated;
    this.renumberElementSteps();

    if (this.hasSavedElementScriptOnce) {
      this.saveElementRecording();
    }
  }

  // ── Action picker (element-driven step) ───────────────────────────────────

  openActionPicker(element: ElementNode): void {
    this.pendingPickerElement = element;
    this.pickerAction = 'tap';
    this.pickerValue = '';
    this.pickerElementName =
      element.attributes?.text ||
      element.attributes?.['content-desc'] ||
      element.tag ||
      `Step ${this.recordedElementSteps.length + 1}`;
    this.showActionPicker = true;
  }

  confirmActionPicker(): void {
    if (!this.pendingPickerElement) {
      this.showActionPicker = false;
      return;
    }

    const { strategy, value } = this.deriveSelector(this.pendingPickerElement);

    const newStep: RecordedElementStep = {
      id: ++this.elementStepCounter,
      step: 0,
      element_name: this.pickerElementName || `Step ${this.recordedElementSteps.length + 1}`,
      selector_strategy: strategy,
      selector_value: value,
      action: this.pickerAction,
      value: this.pickerAction === 'input' ? (this.pickerValue || '') : '',
      duration_ms: this.pickerAction === 'long_press' ? 1000 : null,
      key_code: null,
      swipe_direction: null,
      timeout_ms: 10000,
      elementTag: this.pendingPickerElement.tag,
      runStatus: null,
      runError: null,
    };

    this.insertStepIntoList(newStep, this.insertElementStepAtIndex);
    this.insertElementStepAtIndex = null;
    this.closeActionPicker();
  }

  cancelActionPicker(): void {
    this.insertElementStepAtIndex = null;
    this.closeActionPicker();
  }

  private closeActionPicker(): void {
    this.showActionPicker = false;
    this.pendingPickerElement = null;
  }

  // ── System action picker (Home / Back / Recents / keycode) ───────────────

  openSystemActionPicker(): void {
    this.systemActionType = 'home';
    this.systemActionKeyCode = null;
    this.systemActionName = '';
    this.showSystemActionPicker = true;
  }

  confirmSystemAction(): void {
    const keyCodeMap: Record<string, number> = { home: 3, back: 4, recents: 187 };
    const keyCode = this.systemActionType === 'keycode'
      ? (this.systemActionKeyCode ?? 0)
      : keyCodeMap[this.systemActionType];

    const newStep: RecordedElementStep = {
      id: ++this.elementStepCounter,
      step: 0,
      element_name: this.systemActionName || `${this.systemActionType} action`,
      selector_strategy: null,
      selector_value: null,
      action: 'keycode',
      value: '',
      duration_ms: null,
      key_code: keyCode,
      swipe_direction: null,
      timeout_ms: 10000,
      runStatus: null,
      runError: null,
    };

    this.insertStepIntoList(newStep, this.insertElementStepAtIndex);
    this.insertElementStepAtIndex = null;
    this.showSystemActionPicker = false;
  }

  cancelSystemAction(): void {
    this.insertElementStepAtIndex = null;
    this.showSystemActionPicker = false;
  }

  // ── Insert-in-between ──────────────────────────────────────────────────────

  toggleInsertMenu(index: number): void {
    this.insertMenuIndex = this.insertMenuIndex === index ? null : index;
  }

  startInsertElementStep(index: number): void {
    this.insertElementStepAtIndex = index;
    this.insertMenuIndex = null;
    this.isRecording = true;
    this.showActionResult(true, `Select an element on the screen to insert as step ${index + 1}`);
  }

  startInsertSystemAction(index: number): void {
    this.insertElementStepAtIndex = index;
    this.insertMenuIndex = null;
    this.openSystemActionPicker();
  }

  // ── Deletion (persists via PUT once a script has been saved) ─────────────

  removeElementStep(id: number): void {
    if (this.isPlayingAllElements) return;

    this.recordedElementSteps = this.recordedElementSteps.filter(s => s.id !== id);
    this.renumberElementSteps();

    if (this.hasSavedElementScriptOnce && this.elementScriptId) {
      this.saveElementRecording();
    }
  }

  // ── Save (create on first save, PUT to update afterwards) ────────────────

  async saveElementRecording(): Promise<void> {
    const scriptId = await this.ensureElementScriptInitialized();
    if (!scriptId) {
      this.elementScriptSaveResult = { success: false, message: 'Script not initialized' };
      setTimeout(() => this.elementScriptSaveResult = null, 3000);
      return;
    }

    const steps = this.recordedElementSteps.map(s => ({
      step: s.step,
      element_name: s.element_name,
      selector_strategy: s.selector_strategy,
      selector_value: s.selector_value,
      action: s.action,
      value: s.value || '',
      duration_ms: s.duration_ms,
      key_code: s.key_code,
      swipe_direction: s.swipe_direction,
      timeout_ms: s.timeout_ms ?? 10000,
    }));

    this.isSavingElementScript = true;
    this.elementScriptSaveResult = null;

    try {
      if (!this.hasSavedElementScriptOnce) {
        const url = `${this.baseUrl}/imageRecord/v1/element/record/script/create`;
        const payload = {
          script_id: scriptId,
          script_name: this.elementScriptName || `Script ${scriptId}`,
          mobile_name: this.deviceCapabilities?.['appium:deviceName'] || this.device?.name || '',
          screen: { width: this.deviceResolution.width, height: this.deviceResolution.height },
          email: this.currentUser?.email || '',
          steps,
          appiumSessionId: this.sessionId,
        };
        const response = await this.http.post(url, payload).toPromise() as any;

        if (response?.success) {
          this.hasSavedElementScriptOnce = true;
          this.elementScriptSaveResult = { success: true, message: 'Recording saved successfully' };
          this.msg = 'Recording saved successfully';
          this.openAlertMod(this.alertTemplateStay, this.msg);
        } else {
          this.elementScriptSaveResult = { success: false, message: 'Failed to save recording' };
          this.msg = 'Failed to save recording';
          this.openAlertMod(this.alertTemplateStay, this.msg);
        }
      } else {
        const url = `${this.baseUrl}/imageRecord/v1/element/record/script/${scriptId}`;
        const payload = {
          script_name: this.elementScriptName || `Script ${scriptId}`,
          mobile_name: this.deviceCapabilities?.['appium:deviceName'] || this.device?.name || '',
          screen: { width: this.deviceResolution.width, height: this.deviceResolution.height },
          steps,
          appiumSessionId: this.sessionId,
        };
        const response = await this.http.put(url, payload).toPromise() as any;

        if (response?.success) {
          this.elementScriptSaveResult = { success: true, message: 'Recording updated successfully' };
          this.msg = 'Recording updated successfully';
          this.openAlertMod(this.alertTemplateStay, this.msg);
        } else {
          this.elementScriptSaveResult = { success: false, message: 'Failed to update recording' };
          this.msg = 'Failed to update recording';
          this.openAlertMod(this.alertTemplateStay, this.msg);
        }
      }
    } catch (error) {
      console.error('Failed to save element recording:', error);
      this.elementScriptSaveResult = { success: false, message: 'Failed to save recording' };
    } finally {
      this.isSavingElementScript = false;
      setTimeout(() => this.elementScriptSaveResult = null, 3000);
    }
  }

  private async ensureElementScriptInitialized(): Promise<string | null> {
    if (this.elementScriptId) return this.elementScriptId;

    try {
      const url = `${this.baseUrl}/imageRecord/v1/element/record/script/init`;
      const response = await this.http.post(url, {}).toPromise() as any;

      if (response?.success && response?.script_id) {
        this.elementScriptId = response.script_id;
        return this.elementScriptId;
      }
      console.error('element/record/script/init did not return a script_id:', response);
      this.msg = 'Did not return a script_id';
      this.openAlertMod(this.alertTemplateStay, this.msg);
      return null;
    } catch (error) {
      this.msg = 'Failed to initialize element recording script';
      this.openAlertMod(this.alertTemplateStay, this.msg);
      console.error('Failed to initialize element recording script:', error);
      return null;
    }
  }

  // ── Single-step run ────────────────────────────────────────────────────────

  async runElementStep(step: RecordedElementStep): Promise<void> {
    const scriptId = await this.ensureElementScriptInitialized();
    if (!scriptId) {
      this.showActionResult(false, 'Script not initialized');
      return;
    }

    step.runStatus = 'RUNNING';
    step.runError = null;
    this.recordedElementSteps = [...this.recordedElementSteps];

    try {
      const url = `${this.baseUrl}/imageRecord/v1/element/execute/script/play/step`;
      const payload = {
        appium_session: this.sessionId,
        provider_url: this.appiumServerUrl,
        device_udid: this.device?.serial || '',
        script_id: scriptId,
        steps: [{
          step: step.step,
          element_name: step.element_name,
          selector_strategy: step.selector_strategy,
          selector_value: step.selector_value,
          action: step.action,
          value: step.value || '',
          duration_ms: step.duration_ms,
          key_code: step.key_code,
          swipe_direction: step.swipe_direction,
          timeout_ms: step.timeout_ms ?? 10000,
        }],
      };

      const response = await this.http.post(url, payload).toPromise() as any;

      if (response?.success && response?.execution_id) {
        this.showActionResult(true, response.message || `Running "${step.element_name}"...`);
        this.monitorSingleElementStepExecution(response.execution_id, step);
      } else {
        step.runStatus = 'FAILED';
        step.runError = response?.message || 'Failed to start step';
        this.openAlertMod(this.alertTemplateStay,step.runError);
        this.recordedElementSteps = [...this.recordedElementSteps];
        this.showActionResult(false, step.runError);
      }
    } catch (error) {
      step.runStatus = 'FAILED';
      step.runError = `${error}`;
      this.recordedElementSteps = [...this.recordedElementSteps];
      this.openAlertMod(this.alertTemplateStay,step.runError);
      this.showActionResult(false, `Step execution failed: ${error}`);
    }
  }

  private monitorSingleElementStepExecution(executionId: string, step: RecordedElementStep): void {
    if (this.singleElementStepPollingInterval) {
      clearInterval(this.singleElementStepPollingInterval);
      this.singleElementStepPollingInterval = null;
    }

    this.singleElementStepPollingInterval = setInterval(async () => {
      try {
        const url = `${this.baseUrl}/imageRecord/v1/element/execute/execution/${executionId}`;
        const data = await this.http.get(url).toPromise() as any;

        if (data?.status && ['SUCCESS', 'FAILED', 'STOPPED'].includes(data.status)) {
          const result = (data.step_results || [])[0];

          step.runStatus = data.status === 'SUCCESS' ? 'SUCCESS' : 'FAILED';
          step.runError = result?.error || null;
          this.recordedElementSteps = [...this.recordedElementSteps];

          clearInterval(this.singleElementStepPollingInterval);
          this.singleElementStepPollingInterval = null;

          this.showActionResult(
            step.runStatus === 'SUCCESS',
            step.runStatus === 'SUCCESS'
              ? `Step "${step.element_name}" passed`
              : `Step "${step.element_name}" failed${step.runError ? ': ' + step.runError : ''}`
          );

          setTimeout(() => this.refreshAll(), 500);
          setTimeout(() => {
            step.runStatus = null;
            this.recordedElementSteps = [...this.recordedElementSteps];
          }, 3000);
        }
      } catch (err) {
        console.error('Failed polling single element step execution:', err);
        clearInterval(this.singleElementStepPollingInterval);
        this.singleElementStepPollingInterval = null;
        step.runStatus = 'FAILED';
        step.runError = 'Polling failed';
        this.openAlertMod(this.alertTemplateStay,step.runError);
        this.recordedElementSteps = [...this.recordedElementSteps];
      }
    }, 1000);
  }

  // ── Play All ───────────────────────────────────────────────────────────────

  async playAllElementSteps(): Promise<void> {
    if (this.isPlayingAllElements) {
      await this.stopElementScriptExecution();
      return;
    }

    if (this.recordedElementSteps.length === 0) return;

    const scriptId = await this.ensureElementScriptInitialized();
    if (!scriptId) return;

    await this.saveElementRecording();

    this.isPlayingAllElements = true;
    this.elementExecutionStepResults = [];
    this.currentElementExecutionStep = 0;
    this.totalElementExecutionSteps = this.recordedElementSteps.length;

    try {
      const url = `${this.baseUrl}/imageRecord/v1/element/execute/script/play`;
      const payload = {
        script_id: scriptId,
        appium_session: this.sessionId,
        provider_url: this.appiumServerUrl,
        device_udid: this.device?.serial || '',
      };

      const response = await this.http.post(url, payload).toPromise() as any;

      if (response?.success && response?.execution_id) {
        this.activeElementExecutionId = response.execution_id;
        this.startElementExecutionPolling(response.execution_id);
      } else {
        this.isPlayingAllElements = false;
        console.error('Failed to start element script execution.');
        this.openAlertMod(this.alertTemplateStay,'Failed to start element script execution.');
      }
    } catch (error) {
      console.error('Element playback initialization error:', error);
      this.openAlertMod(this.alertTemplateStay,'Element playback initialization error.');
      this.isPlayingAllElements = false;
    }
  }

  async stopElementScriptExecution(): Promise<void> {
    if (!this.activeElementExecutionId) return;

    try {
      const url = `${this.baseUrl}/imageRecord/v1/element/execute/script/stop`;
      const params = new HttpParams().set('execution_id', this.activeElementExecutionId);
      await this.http.post(url, {}, { params }).toPromise();
    } catch (error) {
      console.error('Failed to stop element script execution:', error);
      this.openAlertMod(this.alertTemplateStay,'Failed to stop element script execution.');
    } finally {
      this.cleanElementExecutionMonitoring();
    }
  }

  private startElementExecutionPolling(executionId: string): void {
    this.cleanElementExecutionMonitoringKeepFlag();
    this.elementExecutionPollingInterval = setInterval(async () => {
      try {
        const url = `${this.baseUrl}/imageRecord/v1/element/execute/execution/${executionId}`;
        const data = await this.http.get(url).toPromise() as any;

        if (data) {
          this.elementExecutionStatus = data.status;
          this.currentElementExecutionStep = data.current_step;
          this.totalElementExecutionSteps = data.total_steps;
          this.elementExecutionStepResults = data.step_results || [];

          if (['SUCCESS', 'FAILED', 'STOPPED'].includes(data.status)) {
            this.cleanElementExecutionMonitoring();
            setTimeout(() => this.refreshAll(), 500);
          }
        }
      } catch (err) {
        console.error('Failed updating element execution status:', err);
        this.openAlertMod(this.alertTemplateStay,'Failed updating element execution status.');
        this.cleanElementExecutionMonitoring();
      }
    }, 1000);
  }

  // Clears the interval only (used right before starting a fresh poll)
  private cleanElementExecutionMonitoringKeepFlag(): void {
    if (this.elementExecutionPollingInterval) {
      clearInterval(this.elementExecutionPollingInterval);
      this.elementExecutionPollingInterval = null;
    }
  }

  private cleanElementExecutionMonitoring(): void {
    this.cleanElementExecutionMonitoringKeepFlag();
    this.isPlayingAllElements = false;
  }

  // ── Repository ──────────────────────────────────────────────────────────────

  /**
   * GET /imageRecord/v1/script/list
   * Fetches lightweight script collection from server.
   */
  async fetchSavedScripts(): Promise<void> {
    this.isScriptsLoading = true;
    try {
      const url = `${this.baseUrl}/imageRecord/v1/script/list`;
      const params = new HttpParams()
        .set('email', this.currentUser.email);
      const data = await this.http
        .get<AutomationScript[]>(url, { params })
        .toPromise();
      this.availableScripts = data || [];
    } catch (error) {
      console.error('Failed retrieving recorded test scripts:', error);
      this.openAlertMod(this.alertTemplateStay,'Failed retrieving recorded test scripts.');
    } finally {
      this.isScriptsLoading = false;
    }
  }

  /**
   * Filters the fetched scripts from left panel list based on the search query input
   */
  get filteredTestCases(): AutomationScript[] {
    const q = this.tcSearchQuery.trim().toLowerCase();
    if (!q) return this.availableScripts;
    return this.availableScripts.filter(script =>
      script.script_name.toLowerCase().includes(q) ||
      script.script_id.toLowerCase().includes(q)
    );
  }

  /**
   * Highlights or pre-selects a item from the script listing
   */
  selectTestCase(script: AutomationScript): void {
    if (this.selectedScript?.script_id === script.script_id) return;
    this.selectedScript = script;
    // Keep steps empty until play execution feedback populates details
    this.scriptSteps = [];
    this.executionLiveStatus = null;
    this.closeWebSocketConnection();
  }

  /**
   * Maps current execution status text strings to respective design tokens
   */
  stepStatusClass(status: string): string {
    const lowerStatus = (status || '').toLowerCase();
    return `step-badge step-${lowerStatus}`;
  }

  /**
   * Summarizes matching processed records counter 
   */
  passedStepsCount(): number {
    return this.scriptSteps.filter(s => s.status?.toLowerCase() === 'success' || s.status?.toLowerCase() === 'passed').length;
  }

  /**
   * POST /imageRecord/v1/script/play
   * Triggers background execution, reveals right visualization deck, 
   * and opens websocket tracking stream.
   */
  async runTestCase(script: AutomationScript): Promise<void> {
    this.selectedScript = script;
    this.executionLiveStatus = 'PENDING';
    this.scriptSteps = [];
    this.closeWebSocketConnection();

    try {
      // First fetch full details of the script to build basic skeleton indices
      const detailsUrl = `${this.baseUrl}/imageRecord/v1/script/${script.script_id}`;
      const scriptDetails = await this.http.get(detailsUrl).toPromise() as any;
      if (scriptDetails?.steps) {
        this.scriptSteps = scriptDetails.steps.map((s: any) => ({
          step_index: s.step ?? s.step_index,
          status: 'NOT_RUN',
          action: s.action,
          element_name: s.element_name ?? s.label ?? s.action, // Fallbacks to ensure text visibility
          image_id: s.image_id ?? s.imageId ?? null,          // Maps incoming image tracking references
          showImage: false,                                   // Keeps the eye preview closed by default
          error: '',
          script_id: script.script_id,
          timestamp: '',
          events: []
        }));
      }else{
        this.openAlertMod(this.alertTemplateStay,'Error fetching the Script Details.')
      }

      // Kicks off device process run configuration
      const playUrl = `${this.baseUrl}/imageRecord/v1/script/play`;
      const payload = {
        script_id: script.script_id,
        udid: this.device?.serial || '',
        capabilities: this.deviceCapabilities || {}
      };

      const response = await this.http.post(playUrl, payload).toPromise() as any;

      if (response?.success && response?.execution_id) {
        this.executionLiveStatus = 'RUNNING';
        this.initializeLiveWebSocket(response.execution_id);
      } else {
        this.executionLiveStatus = 'FAILED';
        this.openAlertMod(this.alertTemplateStay,'Execution initiation rejected by server runner engine.');
        console.error('Execution initiation rejected by server runner engine.');
      }
    } catch (error) {
      console.error('Failed triggering dynamic execution run flow:', error);
      this.executionLiveStatus = 'FAILED';
    }
  }

  /**
   * Toggles individual active view blocks inside your step template container list.
   */
  toggleStepExpand(stepIndex: number): void {
    this.expandedStepIndex = this.expandedStepIndex === stepIndex ? null : stepIndex;
  }

  /**
 * Lazily fetches the step region thumbnail if not already loaded, 
 * then toggles its inline visibility state.
 */
  toggleImagePreview(event: Event, imageId: string, scriptId: string, step: any): void {
    event.stopPropagation(); // Prevent triggering the step click event

    if (!imageId) return;

    // Toggle visibility flag on the step object itself
    step.showImage = !step.showImage;

    // If already fetched previously, do nothing
    if (this.loadedImages[imageId]) return;

    const url = `${this.baseUrl}/imageRecord/v1/image/`;
    const params = new HttpParams()
      .set('script_id', scriptId)
      .set('image_id', imageId);

    this.http.get(url, { params, responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.loadedImages[imageId] = reader.result as string;
        };
        reader.readAsDataURL(blob);
      },
      error: (err) => {
        this.openAlertMod(this.alertTemplateStay,`Failed to fetch image resource for ${imageId}.`);
        console.error(`Failed to fetch image resource for ${imageId}:`, err)
      }
    });
  }

  /**
   * Attaches live listener connecting directly to target communication routing address.
   * Leverages Nginx reverse proxy routing to satisfy HTTPS cross-origin security policies.
   */
  private initializeLiveWebSocket(executionId: string): void {
    const secureHost = window.location.host;
    const wsUrl = `wss://${secureHost}/imageRecord/v1/execution/ws/${executionId}`;

    try {
      this.liveSocket = new WebSocket(wsUrl);

      this.liveSocket.onmessage = (event) => {
        if (!event.data) return;

        try {
          const rawMessage = JSON.parse(event.data);
          this.handleIncomingExecutionLog(rawMessage);
        } catch {
          this.appendLogToCurrentActiveStep(event.data);
        }
      };

      this.liveSocket.onclose = () => {
        console.log(`WebSocket connection closed for execution id: ${executionId}`);
        this.syncFinalExecutionStatus(executionId);
      };

      this.liveSocket.onerror = (err) => {
        console.error('Active execution tracing web socket caught transmission error:', err);
      };
    } catch (wsSetupError) {
      console.error('Failed building native web socket channel reference instance:', wsSetupError);
      this.openAlertMod(this.alertTemplateStay,'Failed to establish live execution tracking channel.');
    }
  }

  /**
   * Dynamically tracks step statuses and hooks logs to them
   */
  private handleIncomingExecutionLog(data: any): void {
    if (data.status) {
      this.executionLiveStatus = data.status;
    }

    const stepIdx = data.step_index ?? data.step;

    // Handle generic global milestones without step contexts (e.g., CONNECTED, SESSION_CREATED)
    if (stepIdx === undefined) {
      const logString = `[${data.timestamp ? new Date(data.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString()}] Event: ${data.event || 'SYSTEM INFO'}`;
      this.systemLogs.push(logString);
      return;
    }

    // Match or construct active script step blocks
    let targetStep = this.scriptSteps.find(s => s.step_index === stepIdx);

    if (!targetStep) {
      targetStep = {
        step_index: stepIdx,
        status: data.status || data.event || 'RUNNING',
        action: data.action || 'Visual Match Engine',
        timestamp: data.timestamp || new Date().toISOString(),
        events: []
      };
      this.scriptSteps.push(targetStep);
      this.scriptSteps.sort((a, b) => a.step_index - b.step_index);
    }

    // Map specific states dynamically
    if (data.status) targetStep.status = data.status;
    if (data.error) targetStep.error = data.error;
    if (data.confidence !== undefined) targetStep.confidence = data.confidence;

    // Build human-readable details strings from standard step event templates
    let eventLogMessage = `Event: ${data.event || 'UPDATE'}`;
    if (data.retry) eventLogMessage += ` (Retry #${data.retry})`;
    if (data.confidence !== undefined) eventLogMessage += ` | Confidence: ${(data.confidence * 100).toFixed(1)}%`;
    if (data.session_id) eventLogMessage += ` | Node: ${data.session_id}`;

    if (!targetStep.events) targetStep.events = [];
    targetStep.events.push(`[${new Date().toLocaleTimeString()}] ${eventLogMessage}`);
  }

  /**
   * Fallback line logging injection mechanism
   */
  private appendLogToCurrentActiveStep(textLog: string): void {
    const activeStep = this.scriptSteps.find(s => s.status === 'RUNNING') ||
      this.scriptSteps.find(s => s.status === 'NOT_RUN');
    if (activeStep) {
      if (!activeStep.events) activeStep.events = [];
      activeStep.events.push(`[${new Date().toLocaleTimeString()}] ${textLog}`);
    } else {
      this.systemLogs.push(`[${new Date().toLocaleTimeString()}] ${textLog}`);
    }
  }

  /**
   * Final statuses checking fallback interface synchronization sweep
   */
  private async syncFinalExecutionStatus(executionId: string): Promise<void> {
    try {
      const url = `${this.baseUrl}/imageRecord/v1/execution/${executionId}`;
      const finalState = await this.http.get(url).toPromise() as any;
      if (finalState) {
        this.executionLiveStatus = finalState.status;
        if (finalState.step_results) {
          finalState.step_results.forEach((res: any) => {
            const match = this.scriptSteps.find(s => s.step_index === res.step_index);
            if (match) {
              match.status = res.status;
              match.error = res.error;
            }
          });
        }
      }else{
        this.openAlertMod(this.alertTemplateStay,'Failed to fetch final execution status.');
      }
    } catch (err) {
      this.openAlertMod(this.alertTemplateStay,'Error conducting validation synchronization sweep.');
      console.error('Error conducting validation synchronization sweep:', err);
    }
  }

  private closeWebSocketConnection(): void {
    if (this.liveSocket) {
      this.liveSocket.close();
      this.liveSocket = null;
    }
  }

  // ── Capabilities Editor ─────────────────────────────────────────────────────

  openCapabilitiesEditor(): void {
    const caps = this.deviceCapabilities || {};
    this.capEditAppName = caps['appium:app'] || caps['appium:appActivity'] || '';
    this.capEditPackageName = caps['appium:appPackage'] || '';
    this.capEditRawJson = JSON.stringify(caps, null, 2);
    this.capJsonError = '';
    this.showCapabilitiesEditor = true;
  }

  onCapJsonChange(): void {
    try {
      const parsed = JSON.parse(this.capEditRawJson);
      this.capEditAppName = parsed['appium:app'] || parsed['appium:appActivity'] || this.capEditAppName;
      this.capEditPackageName = parsed['appium:appPackage'] || this.capEditPackageName;
      this.capJsonError = '';
    } catch {
      this.capJsonError = 'Invalid JSON';
    }
  }

  saveCapabilities(): void {
    try {
      const parsed = JSON.parse(this.capEditRawJson);
      if (this.capEditAppName.trim()) {
        parsed['appium:app'] = this.capEditAppName.trim();
      }
      if (this.capEditPackageName.trim()) {
        parsed['appium:appPackage'] = this.capEditPackageName.trim();
      }
      this.deviceCapabilities = parsed;
      this.capEditRawJson = JSON.stringify(parsed, null, 2);
      this.capJsonError = '';
      this.showCapabilitiesEditor = false;
    } catch {
      this.capJsonError = 'Invalid JSON — please fix before saving.';
    }
  }

  closeCapabilitiesEditor(): void {
    this.showCapabilitiesEditor = false;
    this.capJsonError = '';
  }

  // ── Image Recorder ──────────────────────────────────────────────────────────

  toggleImageSelectMode(): void {
    this.imageSelectMode = !this.imageSelectMode;
    if (!this.imageSelectMode) {
      this.clearOverlayCanvas();
      this.isDraggingRegion = false;
      this.selectionStart = null;
      this.selectionCurrent = null;
    }
  }

  onSelectionStart(event: MouseEvent): void {
    this.syncOverlaySize();
    const pos = this.getOverlayCanvasPos(event);
    this.isDraggingRegion = true;
    this.selectionStart = pos;
    this.selectionCurrent = pos;
    event.preventDefault();
  }

  onSelectionMove(event: MouseEvent): void {
    if (!this.isDraggingRegion) return;
    this.selectionCurrent = this.getOverlayCanvasPos(event);
    this.drawSelectionRect();
  }

  onSelectionEnd(event: MouseEvent): void {
    if (!this.isDraggingRegion) return;
    this.isDraggingRegion = false;
    this.selectionCurrent = this.getOverlayCanvasPos(event);
    this.captureRegion();
    this.clearOverlayCanvas();
  }

  onSelectionCancel(): void {
    if (!this.isDraggingRegion) return;
    this.isDraggingRegion = false;
    this.selectionStart = null;
    this.selectionCurrent = null;
    this.clearOverlayCanvas();
  }

  private syncOverlaySize(): void {
    const main = this.canvasRef?.nativeElement;
    const overlay = this.overlayCanvasRef?.nativeElement;
    if (!main || !overlay) return;
    overlay.width = main.width;
    overlay.height = main.height;
  }

  private getOverlayCanvasPos(event: MouseEvent): { canvasX: number; canvasY: number } {
    const overlay = this.overlayCanvasRef?.nativeElement;
    if (!overlay) return { canvasX: 0, canvasY: 0 };
    const rect = overlay.getBoundingClientRect();
    return {
      canvasX: (event.clientX - rect.left) * (overlay.width / rect.width),
      canvasY: (event.clientY - rect.top) * (overlay.height / rect.height),
    };
  }

  private drawSelectionRect(): void {
    const overlay = this.overlayCanvasRef?.nativeElement;
    if (!overlay || !this.selectionStart || !this.selectionCurrent) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, overlay.width, overlay.height);

    const x = Math.min(this.selectionStart.canvasX, this.selectionCurrent.canvasX);
    const y = Math.min(this.selectionStart.canvasY, this.selectionCurrent.canvasY);
    const w = Math.abs(this.selectionCurrent.canvasX - this.selectionStart.canvasX);
    const h = Math.abs(this.selectionCurrent.canvasY - this.selectionStart.canvasY);

    ctx.fillStyle = 'rgba(0,0,0,0.42)';
    ctx.fillRect(0, 0, overlay.width, overlay.height);
    ctx.clearRect(x, y, w, h);

    ctx.strokeStyle = '#167FFC';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 3]);
    ctx.strokeRect(x, y, w, h);

    ctx.setLineDash([]);
    ctx.fillStyle = '#167FFC';
    const hs = 7;
    [[x, y], [x + w, y], [x, y + h], [x + w, y + h]].forEach(([cx, cy]) => {
      ctx.fillRect(cx - hs / 2, cy - hs / 2, hs, hs);
    });
  }

  private captureRegion(): void {
    if (!this.selectionStart || !this.selectionCurrent) return;
    const main = this.canvasRef?.nativeElement;
    if (!main) return;

    const x = Math.min(this.selectionStart.canvasX, this.selectionCurrent.canvasX);
    const y = Math.min(this.selectionStart.canvasY, this.selectionCurrent.canvasY);
    const w = Math.abs(this.selectionCurrent.canvasX - this.selectionStart.canvasX);
    const h = Math.abs(this.selectionCurrent.canvasY - this.selectionStart.canvasY);

    if (w < 5 || h < 5) return;

    const temp = document.createElement('canvas');
    temp.width = Math.round(w);
    temp.height = Math.round(h);
    const tempCtx = temp.getContext('2d');
    if (!tempCtx) return;

    tempCtx.drawImage(main, x, y, w, h, 0, 0, w, h);

    this.imageRegionCounter++;
    const newRegion: ImageRegion = {
      id: this.imageRegionCounter,
      label: `Region ${this.imageRegionCounter}`,
      x: Math.round(x / this.scaleFactor.x),
      y: Math.round(y / this.scaleFactor.y),
      width: Math.round(w / this.scaleFactor.x),
      height: Math.round(h / this.scaleFactor.y),
      dataUrl: temp.toDataURL('image/png'),
      action: 'img_tap',
      value: '',
      image_id: null,
      isUploading: false,
      uploadError: null,
      confidence: 1,
    };

    // NEW — insert-in-between logic
    const wasInsertion = this.insertAtIndex !== null
      && this.insertAtIndex >= 0
      && this.insertAtIndex <= this.imageRegions.length;

    if (wasInsertion) {
      const updated = [...this.imageRegions];
      updated.splice(this.insertAtIndex as number, 0, newRegion);
      this.imageRegions = updated;
      this.insertAtIndex = null;
    } else {
      this.imageRegions = [...this.imageRegions, newRegion];
    }

    this.selectionStart = null;
    this.selectionCurrent = null;
    this.activeTab = 'image-recorder';

    // Fire-and-forget upload; if this was a mid-list insertion, persist the
    // new step order to the backend once the upload resolves.
    this.uploadRegionImage(newRegion).then(() => {
      if (wasInsertion && this.hasSavedScriptOnce) {
        this.saveImageScript();
      }
    });
  }

  /**
   * Called by the hover "+" button between two region cards.
   * Arms the next captured region to be spliced into the list at `index`
   * instead of appended at the end, then enables select mode if needed.
   */
  addStepAt(index: number): void {
    if (this.isPlayingAll) return;
    this.insertAtIndex = index;
    if (!this.imageSelectMode) {
      this.toggleImageSelectMode();
    }
    this.showActionResult(true, `Draw a region to insert as step ${index + 1}`);
  }

  // ── Image Recorder — backend script binding ─────────────────────────────────

  /**
   * POST /imageRecord/v1/script/init
   * Lazily initialises a recording session and stores the returned script_id.
   * Safe to call multiple times — only initialises once per component lifetime.
   */
  private async ensureScriptInitialized(): Promise<string | null> {
    if (this.imageScriptId) return this.imageScriptId;

    this.isInitializingScript = true;
    try {
      const url = `${this.baseUrl}/imageRecord/v1/script/init`;
      const response = await this.http.post(url, {}).toPromise();
      const result = response as any;

      if (result?.success && result?.script_id) {
        this.imageScriptId = result.script_id;
        return this.imageScriptId;
      }

      console.error('script/init did not return a script_id:', result);
      this.openAlertMod(this.alertTemplateStay,'script_id is null.');
      return null;
    } catch (error) {
      console.error('Failed to initialize image recording script:', error);
      this.openAlertMod(this.alertTemplateStay,'Failed to initialize image recording script');
      return null;
    } finally {
      this.isInitializingScript = false;
    }
  }

  /**
   * POST /imageRecord/v1/image/upload
   * Uploads a captured region's PNG to the storage service and stores
   * the returned image_id on the region.
   */
  private async uploadRegionImage(region: ImageRegion): Promise<void> {
    const scriptId = await this.ensureScriptInitialized();
    if (!scriptId) {
      region.uploadError = 'Could not initialize script';
      this.imageRegions = [...this.imageRegions];
      return;
    }

    region.isUploading = true;
    region.uploadError = null;
    this.imageRegions = [...this.imageRegions];

    try {
      const blob = await this.dataUrlToBlob(region.dataUrl);

      const formData = new FormData();
      formData.append('script_id', scriptId);
      formData.append('file', blob, `${region.label || 'region'}.png`);

      const url = `${this.baseUrl}/imageRecord/v1/image/upload`;
      const response = await this.http.post(url, formData).toPromise();
      const result = response as any;

      if (result?.success && result?.image_id) {
        region.image_id = result.image_id;
      } else {
        this.openAlertMod(this.alertTemplateStay,'Upload failed: ' + (result?.message || 'Unknown error'));
        region.uploadError = 'Upload failed';
      }
    } catch (error) {
      console.error('Failed to upload region image:', error);
      this.openAlertMod(this.alertTemplateStay,'Upload failed: ' + error);
      region.uploadError = 'Upload failed';
    } finally {
      region.isUploading = false;
      this.imageRegions = [...this.imageRegions];
    }
  }

  private dataUrlToBlob(dataUrl: string): Promise<Blob> {
    return fetch(dataUrl).then(res => res.blob());
  }

  /**
   * POST /imageRecord/v1/script/create  (first save)
   * PUT  /imageRecord/v1/script/{script_id}  (subsequent saves)
   *
   * Persists the full set of image-recorder steps for this script.
   * Steps without an image_id (still uploading / failed) are skipped.
   */
  async saveImageScript(): Promise<void> {
    const scriptId = await this.ensureScriptInitialized();
    if (!scriptId) {
      this.scriptSaveResult = { success: false, message: 'Script not initialized' };
      setTimeout(() => this.scriptSaveResult = null, 3000);
      return;
    }

    const readyRegions = this.imageRegions.filter(r => !!r.image_id);
    if (readyRegions.length < this.imageRegions.length) {
      console.warn('Some regions are still uploading or failed and will be skipped from save.');
    }

    const steps = readyRegions.map((r, idx) => ({
      step: idx + 1,
      image_id: r.image_id as string,
      element_name: r.label,
      action: r.action,
      value: r.value || '',
      confidence: r.confidence ?? 1,
    }));

    this.isSavingScript = true;
    this.scriptSaveResult = null;

    try {
      if (!this.hasSavedScriptOnce) {
        // First save → script/create
        const url = `${this.baseUrl}/imageRecord/v1/script/create`;
        const payload = {
          appiumSessionId: this.sessionId,
          script_id: scriptId,
          script_name: this.imageScriptName || `Script ${scriptId}`,
          mobile_name: this.deviceCapabilities?.['appium:deviceName'] || this.device?.name || '',
          email: this.currentUser.email || '',
          screen: {
            width: this.deviceResolution.width,
            height: this.deviceResolution.height,
          },
          steps,
        };
        const response = await this.http.post(url, payload).toPromise();
        const result = response as any;

        if (result?.success) {
          this.hasSavedScriptOnce = true;
          this.scriptSaveResult = { success: true, message: 'Script saved successfully' };
        } else {
          this.scriptSaveResult = { success: false, message: 'Failed to save script' };
          this.openAlertMod(this.alertTemplateStay,'Failed to save script: ' + (result?.message || 'Unknown error'));
        }
      } else {
        // Subsequent saves → script/{script_id} update (PUT)
        const url = `${this.baseUrl}/imageRecord/v1/script/${scriptId}`;
        const payload = {
          appiumSessionId: this.sessionId,
          script_name: this.imageScriptName || `Script ${scriptId}`,
          mobile_name: this.deviceCapabilities?.['appium:deviceName'] || this.device?.name || '',
          screen: {
            width: this.deviceResolution.width,
            height: this.deviceResolution.height,
          },
          steps,
        };
        const response = await this.http.put(url, payload).toPromise();
        const result = response as any;

        if (result?.success) {
          this.scriptSaveResult = { success: true, message: 'Script updated successfully' };
        } else {
          this.scriptSaveResult = { success: false, message: 'Failed to update script' };
          this.openAlertMod(this.alertTemplateStay,'Failed to update script: ' + (result?.message || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Failed to save image recording script:', error);
      this.scriptSaveResult = { success: false, message: 'Failed to save script' };
      this.openAlertMod(this.alertTemplateStay,'Failed to save script: ' + error);
    } finally {
      this.isSavingScript = false;
      setTimeout(() => this.scriptSaveResult = null, 3000);
    }
  }

  private hasSavedScriptOnce: boolean = false;

  private clearOverlayCanvas(): void {
    const overlay = this.overlayCanvasRef?.nativeElement;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, overlay.width, overlay.height);
  }

  /**
   * DELETE /imageRecord/v1/image/{image_id}
   * Removes the region locally and, if it was uploaded, deletes the
   * underlying image + metadata from the backend too.
   */
  removeImageRegion(id: number): void {
    const region = this.imageRegions.find(r => r.id === id);
    this.imageRegions = this.imageRegions.filter(r => r.id !== id);

    if (region?.image_id) {
      const url = `${this.baseUrl}/imageRecord/v1/image/${region.image_id}`;
      this.http.delete(url).subscribe({
        next: () => console.log('Image deleted:', region.image_id),
        error: (error) => {
          this.openAlertMod(this.alertTemplateStay,'Failed to delete image: ' + error);
          console.error('Failed to delete image:', error)
        }
      });
    }
  }

  /**
   * POST /imageRecord/v1/script/play/step
   * Runs a single region's step against the live device via the backend
   * execution engine (rather than driving raw W3C actions from the browser).
   * Returns an execution_id immediately; we poll for its terminal status.
   */
  async performImageRegionAction(region: ImageRegion): Promise<void> {
    const scriptId = await this.ensureScriptInitialized();
    if (!scriptId) {
      this.showActionResult(false, 'Script not initialized');
      return;
    }

    if (!region.image_id) {
      this.showActionResult(false, `Region "${region.label}" is still uploading — please wait`);
      return;
    }

    this.isPerformingAction = true;
    this.lastActionResult = null;
    this.playingRegionId = region.id;

    const stepIndex = this.imageRegions.findIndex(r => r.id === region.id);
    console.log(`Performing action for region "${region.label}" at index ${stepIndex} and region.action = ${region.action}`);
    const step = {
      step: stepIndex + 1,
      image_id: region.image_id,
      element_name: region.label,
      action: region.action,
      value: region.value || '',
      confidence: region.confidence ?? 1,
    };

    try {
      const url = `${this.baseUrl}/imageRecord/v1/script/play/step`;
      const payload = {
        appium_session: this.sessionId,
        steps: [step],
        provider_url: this.appiumServerUrl,
        device_udid: this.device?.serial || '',
        script_id: scriptId,
      };

      const response = await this.http.post(url, payload).toPromise() as any;

      if (response?.success && response?.execution_id) {
        this.showActionResult(true, response.message || `Running step "${region.label}"...`);
        this.monitorSingleStepExecution(response.execution_id, region);
      } else {
        this.openAlertMod(this.alertTemplateStay,'Failed to start step execution: ' + (response?.message || 'Unknown error'));
        this.showActionResult(false, response?.message || 'Failed to start step execution');
        this.playingRegionId = null;
      }
    } catch (error) {
      this.openAlertMod(this.alertTemplateStay,'Failed to run image region step: ' + error);
      console.error('Failed to run image region step:', error);
      this.showActionResult(false, `Step execution failed: ${error}`);
      this.playingRegionId = null;
    } finally {
      this.isPerformingAction = false;
    }
  }

  /**
   * Polls GET /imageRecord/v1/execution/{execution_id} until the single-step
   * run reaches a terminal state, then reflects pass/fail on the region card.
   * (Swap this for a WebSocket subscription later if you want live progress
   * instead of 1s polling — same shape as initializeLiveWebSocket.)
   */
  private monitorSingleStepExecution(executionId: string, region: ImageRegion): void {
    if (this.singleStepPollingInterval) {
      clearInterval(this.singleStepPollingInterval);
      this.singleStepPollingInterval = null;
    }

    region.runStatus = 'RUNNING';
    region.runError = null;
    this.imageRegions = [...this.imageRegions];

    this.singleStepPollingInterval = setInterval(async () => {
      try {
        const url = `${this.baseUrl}/imageRecord/v1/execution/${executionId}`;
        const data = await this.http.get(url).toPromise() as any;

        if (data?.status && ['SUCCESS', 'FAILED', 'STOPPED'].includes(data.status)) {
          const stepResult = (data.step_results || [])[0];

          region.runStatus = data.status === 'SUCCESS' ? 'SUCCESS' : 'FAILED';
          region.runError = stepResult?.error || null;
          this.imageRegions = [...this.imageRegions];

          clearInterval(this.singleStepPollingInterval);
          this.singleStepPollingInterval = null;
          this.playingRegionId = null;

          this.showActionResult(
            region.runStatus === 'SUCCESS',
            region.runStatus === 'SUCCESS'
              ? `Step "${region.label}" passed`
              : `Step "${region.label}" failed${region.runError ? ': ' + region.runError : ''}`
          );

          setTimeout(() => this.refreshAll(), 500);

          // Clear the badge after a moment so it doesn't stay forever
          setTimeout(() => {
            region.runStatus = null;
            this.imageRegions = [...this.imageRegions];
          }, 3000);
        }
      } catch (err) {
        this.openAlertMod(this.alertTemplateStay,'Polling failed: ' + err);
        console.error('Failed polling single-step execution status:', err);
        clearInterval(this.singleStepPollingInterval);
        this.singleStepPollingInterval = null;
        this.playingRegionId = null;
        region.runStatus = 'FAILED';
        region.runError = 'Polling failed';
        this.imageRegions = [...this.imageRegions];
      }
    }, 1000);
  }

  /**
   * Sends a text string to whatever element sits at the given device
   * coordinates. Used by the "Input" action type in the Image Recorder.
   */
  private async sendKeysAtCoordinates(x: number, y: number, value: string): Promise<void> {
    if (!value) {
      this.showActionResult(false, 'No input value set for this region');
      return;
    }

    this.isPerformingAction = true;
    this.lastActionResult = null;

    try {
      // Tap first to focus the field at this position
      await this.tapByCoordinates(x, y);

      // Find element at this point (if page source is available) to send keys to it directly
      const element = this.findElementAtPoint(x, y);

      if (element) {
        const previousSelected = this.selectedElement;
        this.selectedElement = element;

        const elementId = await this.findElement();
        if (elementId) {
          const url = `${this.appiumServerUrl}/session/${this.sessionId}/element/${elementId}/value`;
          await this.http.post(url, { text: value }).toPromise();
          this.showActionResult(true, `Sent "${value}" to element`);
        } else {
          this.showActionResult(false, 'Element not found for input');
        }

        this.selectedElement = previousSelected;
      } else {
        this.showActionResult(false, 'No element found at region for input');
      }
    } catch (error) {
      this.openAlertMod(this.alertTemplateStay,'Input failed: ' + error);
      console.error('Failed to send keys at coordinates:', error);
      this.showActionResult(false, `Input failed: ${error}`);
    } finally {
      this.isPerformingAction = false;
    }
  }

  /**
   * Removes all regions locally and deletes each uploaded image from the backend.
   */
  clearImageRegions(): void {
    const uploaded = this.imageRegions.filter(r => r.image_id);

    for (const region of uploaded) {
      const url = `${this.baseUrl}/imageRecord/v1/image/${region.image_id}`;
      this.http.delete(url).subscribe({
        next: () => console.log('Image deleted:', region.image_id),
        error: (error) => {
          this.openAlertMod(this.alertTemplateStay,'Failed to delete image: ' + error);
          console.error('Failed to delete image:', error);
        }
      });
    }

    this.imageRegions = [];
  }

  // ── Play — Execution Controls ──────────────────────────────────────────────────

  /**
   * POST /imageRecord/v1/script/play
   * Starts server-side image script automation execution framework.
   */
  async playAllImageRegions(): Promise<void> {
    if (this.isPlayingAll) {
      // Toggle logic: acting as stop button when clicked during run
      await this.stopScriptExecution();
      return;
    }

    const scriptId = await this.ensureScriptInitialized();
    if (!scriptId) {
      console.error('Cannot run script: scriptId context missing.');
      return;
    }

    // Save current script progress first to make sure steps match local adjustments
    await this.saveImageScript();

    this.isPlayingAll = true;
    this.shouldStopPlayback = false;

    try {
      const url = `${this.baseUrl}/imageRecord/v1/script/play`;
      const payload = {
        script_id: scriptId,
        udid: this.device?.serial || '',
        capabilities: this.deviceCapabilities || {}
      };

      const response = await this.http.post(url, payload).toPromise();
      const result = response as any;

      if (result?.success && result?.execution_id) {
        this.activeExecutionId = result.execution_id;
        this.startExecutionPolling(result.execution_id);
      } else {
        this.isPlayingAll = false;
        this.openAlertMod(this.alertTemplateStay,'Failed to kickstart remote automated execution session: ' + (result?.message || 'Unknown error'));
        console.error('Failed to kickstart remote automated execution session.');
      }
    } catch (error) {
      console.error('Playback initialization error:', error);
      this.isPlayingAll = false;
    }
  }

  /**
   * POST /imageRecord/v1/script/stop
   * Stops active server-side automation session.
   */
  async stopScriptExecution(): Promise<void> {
    if (!this.activeExecutionId) return;
    try {
      const url = `${this.baseUrl}/imageRecord/v1/script/stop`;
      const response = await this.http.post(url, { execution_id: this.activeExecutionId }).toPromise();
      const result = response as any;
      if (result?.success) {
        this.shouldStopPlayback = true;
        this.cleanExecutionMonitoring();
      }
    } catch (error) {
      this.openAlertMod(this.alertTemplateStay,'Failed to halt execution runtime context safely: ' + error);
      console.error('Failed to halt execution runtime context safely:', error);
    }
  }

  /**
   * POST /imageRecord/v1/execution/{execution_id}/pause
   * Pauses running automated processing context.
   */
  async pauseScriptExecution(): Promise<void> {
    if (!this.activeExecutionId) return;
    try {
      const url = `${this.baseUrl}/imageRecord/v1/execution/${this.activeExecutionId}/pause`;
      await this.http.post(url, {}).toPromise();
    } catch (error) {
      this.openAlertMod(this.alertTemplateStay,'Failed to pause execution: ' + error);
      console.error('Error dispatching pause command context flag.', error);
    }
  }

  /**
   * POST /imageRecord/v1/execution/{execution_id}/resume
   * Resumes a paused execution run.
   */
  async resumeScriptExecution(): Promise<void> {
    if (!this.activeExecutionId) return;
    try {
      const url = `${this.baseUrl}/imageRecord/v1/execution/${this.activeExecutionId}/resume`;
      await this.http.post(url, {}).toPromise();
    } catch (error) {
      this.openAlertMod(this.alertTemplateStay,'Failed to resume execution: ' + error);
      console.error('Error dispatching resume command context flag.', error);
    }
  }

  /**
   * Loops interval tracking calls targeting GET status endpoints
   * until processing reaches termination limits.
   */
  private startExecutionPolling(executionId: string): void {
    this.cleanExecutionMonitoring();
    this.executionPollingInterval = setInterval(async () => {
      try {
        const url = `${this.baseUrl}/imageRecord/v1/execution/${executionId}`;
        const data = await this.http.get(url).toPromise() as any;

        if (data) {
          this.executionStatus = data.status;
          this.currentExecutionStep = data.current_step;
          this.totalExecutionSteps = data.total_steps;
          this.executionStepResults = data.step_results || [];

          // Map the backend step counter back to local highlighting logic
          if (this.executionStepResults.length > 0) {
            const runningIndex = data.current_step;
            if (this.imageRegions[runningIndex]) {
              this.playingRegionId = this.imageRegions[runningIndex].id;
            }
          }

          // Termination boundaries validation
          if (['SUCCESS', 'FAILED', 'STOPPED'].includes(data.status)) {
            this.cleanExecutionMonitoring();
          }
        }
      } catch (err) {
        this.openAlertMod(this.alertTemplateStay,'Polling failed: ' + err);
        console.error('Failed updating tracking bounds monitoring layer stream:', err);
        this.cleanExecutionMonitoring();
      }
    }, 1000);
  }

  private cleanExecutionMonitoring(): void {
    if (this.executionPollingInterval) {
      clearInterval(this.executionPollingInterval);
      this.executionPollingInterval = null;
    }
    this.isPlayingAll = false;
    this.playingRegionId = null;
  }

  async endSession(): Promise<void> {
    this.showCloseSessionAlert = true;
    this.cleanExecutionMonitoring();
    try {
      const url = `${this.appiumServerUrl}/session/${this.sessionId}`;
      this.http.delete(url).subscribe(() => {
        this.showCloseSessionAlert = false;
        console.log('Session ended successfully');
        sessionStorage.removeItem("appiumSessionId");
        sessionStorage.removeItem("payloadAppiumSession");
        sessionStorage.removeItem("providerUrl");
        this.commonService.setActiveTab('/control/');
        this.router.navigate(['/control', this.device.serial]);
      });
    } catch (error) {
      console.error('Failed to end session:', error);
      this.showCloseSessionAlert = false;
      this.msg = error;
      this.openAlertMod(this.alertTemplate2, this.msg);
    }
  }

  openAlertMod(template: TemplateRef<any>, message: any) {
    this.modalRef = this.dialog.open(template, { width: '300px' });
    this.msg = message;
  }

  Ok() {
    this.modalRef.close();
    this.msg = "";
    this.commonService.setActiveTab('/control/');
    this.router.navigate(['/control', this.device.serial]);
  }

  OkAndRouteToDevices() {
    this.modalRef.close();
    this.msg = "";
    this.commonService.setActiveTab('/devices');
    this.router.navigate(['/devices']);
  }

  OKAndStay() {
    this.modalRef.close();
    this.msg = "";
  }

}